import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, API, downloadPdf } from "@/lib/api";
import { uploadFileChunked } from "@/lib/chunkedUpload";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import SiteFooter from "@/components/app/SiteFooter";
import ThemedBackground from "@/components/app/ThemedBackground";
import { applyTheme } from "@/lib/themes";
import Comunicacion from "@/pages/Comunicacion";
import ChatWidget from "@/components/app/ChatWidget";
import DocenteMessages from "@/components/app/DocenteMessages";
import PushToggle from "@/components/app/PushToggle";
import FabDock from "@/components/app/FabDock";
import NotificationCenter from "@/components/app/NotificationCenter";
import PushPrompt from "@/components/app/PushPrompt";
import MusicModule from "@/components/app/MusicModule";
import { GroupAttendanceDownload } from "@/components/app/GroupAttendanceDownload";
import PlaneacionEvaluacion from "@/components/app/PlaneacionEvaluacion";
import PlaneacionAreas from "@/components/app/PlaneacionAreas";
import PlaneacionFormato from "@/components/app/PlaneacionFormato";
import PlaneacionIngles from "@/components/app/PlaneacionIngles";
import SignatureUploader from "@/components/app/SignatureUploader";
import LogoSplash from "@/components/app/LogoSplash";
import BancoRecursos from "@/components/app/docente/BancoRecursos";
import Dosificacion from "@/components/app/docente/Dosificacion";
import LibroCalificaciones from "@/components/app/docente/LibroCalificaciones";
import {
  GraduationCap, LogOut, ClipboardCheck, BookOpen, AlertTriangle, Video, MonitorPlay,
  Home, Check, X, Clock3, ShieldCheck, Loader2, Users, CalendarDays, Save, Sparkles,
  FileText, Image as ImageIcon, Eye, Camera, Send, Trash2, Music, Copy, Star, CheckCircle2, Clock,
  Presentation, Paperclip, Link2, Youtube, Plus, CalendarClock, Pencil, Megaphone,
  FolderOpen, ExternalLink, Download, Award, MessageCircle, ArrowLeft, Calculator, Car, RotateCcw, CheckCheck,
} from "lucide-react";

// Etiqueta legible de la clave de grupo compuesta "1A|Preescolar" -> "1A · Preescolar".
const groupLabel = (v) => (v && v.includes("|") ? v.split("|").join(" · ") : v || "");

const NAV = [
  { id: "inicio", label: "Inicio", icon: Home },
  { id: "asistencia", label: "Asistencia", icon: ClipboardCheck },
  { id: "aula", label: "Aula Virtual", icon: Presentation },
  { id: "recursos", label: "Banco de Recursos", icon: FolderOpen },
  { id: "planeaciones", label: "Planeaciones", icon: FileText },
  { id: "dosificacion", label: "Dosificación", icon: CalendarClock },
  { id: "clubs", label: "Clubs", icon: Award },
  { id: "calificaciones", label: "Calificaciones", icon: Calculator },
  { id: "boletas", label: "Boletas", icon: BookOpen },
  { id: "incidencias", label: "Bitácora Digital", icon: AlertTriangle },
  { id: "mensajes", label: "Mensajes", icon: MessageCircle },
  { id: "clases", label: "Clases en línea", icon: MonitorPlay },
  { id: "reunion", label: "Reunión digital", icon: Video },
  { id: "musica", label: "Música", icon: Music },
];


const STATUS = {
  presente: { label: "Presente", short: "Presente", cls: "bg-emerald-500 text-white", ring: "ring-emerald-500", icon: Check },
  ausente: { label: "Ausente", short: "Ausente", cls: "bg-red-500 text-white", ring: "ring-red-500", icon: X },
  retardo: { label: "Asistencia con retardo", short: "Retardo", cls: "bg-amber-500 text-white", ring: "ring-amber-500", icon: Clock3 },
  justificado: { label: "Justificado", short: "Justificado", cls: "bg-sky-500 text-white", ring: "ring-sky-500", icon: ShieldCheck },
};
const ORDER = ["presente", "ausente", "retardo", "justificado"];
const today = () => new Date().toISOString().slice(0, 10);

/* ---------------- Asistencia ---------------- */
function Asistencia({ profile }) {
  const grades = useMemo(() => {
    const assigned = profile?.groups || [];
    if (assigned.length) return assigned;
    return (profile?.all_grades || []).map((g) => g.grade);
  }, [profile]);
  const [grade, setGrade] = useState("");
  const [date, setDate] = useState(today());
  const [students, setStudents] = useState([]);
  const [records, setRecords] = useState({});
  const [savedAt, setSavedAt] = useState(null);
  const [savedBy, setSavedBy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [isTitular, setIsTitular] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => { if (!grade && grades.length) setGrade(grades[0]); }, [grades]); // eslint-disable-line

  const load = async () => {
    if (!grade) return;
    setLoading(true);
    try {
      const { data } = await api.get("/docente/attendance", { params: { grade, date } });
      setStudents(data.students || []);
      setRecords(data.records || {});
      setSavedAt(data.saved_at || null);
      setSavedBy(data.saved_by || null);
      setIsTitular(!!data.is_titular);
    } catch { toast.error("No se pudo cargar la asistencia"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [grade, date]); // eslint-disable-line

  const cycle = (sid) => setRecords((r) => {
    const cur = r[sid];
    const idx = ORDER.indexOf(cur);
    const next = idx < 0 ? ORDER[0] : ORDER[(idx + 1) % ORDER.length];
    return { ...r, [sid]: next };
  });
  const setStatus = (sid, st) => setRecords((r) => ({ ...r, [sid]: st }));
  const markAll = (st) => setRecords(() => Object.fromEntries(students.map((s) => [s.id, st])));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/docente/attendance", { grade, date, records });
      setSavedAt(data.saved_at);
      setSavedBy(profile?.name);
      const s = data.summary;
      const retTxt = s.retardo > 0 ? ` (incluye ${s.retardo} con retardo)` : "";
      toast.success(`Asistencia guardada · ${s.presente} asistieron${retTxt}, ${s.ausente} ausentes`);
    } catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const counts = ORDER.reduce((a, k) => ({ ...a, [k]: Object.values(records).filter((v) => v === k).length }), {});

  const dateLabel = new Date(date + "T00:00:00").toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" });
  const logoSrc = profile?.logo_url ? `${API}${profile.logo_url}` : null;

  const exportPDF = () => {
    if (!grade) return;
    downloadPdf("/docente/attendance/pdf", { params: { grade, date }, filename: `asistencia_${grade}_${date}.pdf` });
  };
  const exportImage = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const png = await toPng(reportRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#ffffff" });
      const a = document.createElement("a");
      a.href = png;
      a.download = `asistencia_${grade}_${date}.png`;
      a.click();
      toast.success("Imagen descargada");
    } catch { toast.error("No se pudo generar la imagen"); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-5" data-testid="docente-asistencia">
      <div className="doc-glass rounded-2xl p-5 border border-white/50 shadow-lg">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Grupo</label>
            <select data-testid="asis-grade" value={grade} onChange={(e) => setGrade(e.target.value)} className="doc-input min-w-[8rem]">
              {grades.length === 0 && <option value="">Sin grupos</option>}
              {grades.map((g) => <option key={g} value={g}>{groupLabel(g)}</option>)}
            </select>
            {grade && (
              <span data-testid="asis-role-badge" className={`inline-block mt-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full ${isTitular ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`}>
                {isTitular ? "★ Titular del grupo" : "Maestro de asignatura (lista independiente)"}
              </span>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Fecha</label>
            <input data-testid="asis-date" type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} className="doc-input" />
          </div>
          <div className="flex-1 min-w-[10rem] flex flex-wrap gap-2 justify-end">
            <button onClick={() => markAll("presente")} data-testid="asis-all-present" className="text-xs inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg font-medium"><Check className="w-3.5 h-3.5" /> Todos presentes</button>
            <button onClick={save} disabled={saving || !grade || students.length === 0} data-testid="asis-save" className="text-xs inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
            </button>
            <button onClick={exportPDF} disabled={!grade || students.length === 0} data-testid="asis-pdf" className="text-xs inline-flex items-center gap-1.5 border border-navy text-navy hover:bg-navy hover:text-white px-3 py-2 rounded-lg font-semibold disabled:opacity-50 transition-colors">
              <FileText className="w-4 h-4" /> PDF
            </button>
            <button onClick={exportImage} disabled={exporting || !grade || students.length === 0} data-testid="asis-image" className="text-xs inline-flex items-center gap-1.5 border border-navy text-navy hover:bg-navy hover:text-white px-3 py-2 rounded-lg font-semibold disabled:opacity-50 transition-colors">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} Imagen
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4 text-[11px]">
          {ORDER.map((k) => <span key={k} className={`px-2.5 py-1 rounded-full ${STATUS[k].cls} bg-opacity-90`}>{STATUS[k].label}: {counts[k] || 0}</span>)}
          <span className="px-2.5 py-1 rounded-full bg-navy/10 text-navy">Total: {students.length}</span>
        </div>
        {savedAt && <div className="text-[11px] text-nardo-500 mt-2">Última vez guardado por {savedBy || "docente"} · {new Date(savedAt).toLocaleString("es-MX")} · se comparte con Dirección en tiempo real.</div>}
      </div>

      <div className="doc-glass rounded-2xl border border-white/50 shadow-lg overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…</div>
        ) : students.length === 0 ? (
          <div className="py-16 text-center text-nardo-500 text-sm">No hay alumnos en este grupo.</div>
        ) : (
          <div className="divide-y divide-white/40">
            {students.map((s, i) => {
              const st = records[s.id];
              return (
                <div key={s.id} className="flex items-center gap-3 px-4 py-3" data-testid={`asis-row-${s.id}`}>
                  <div className="w-7 text-center text-[11px] text-nardo-400">{i + 1}</div>
                  <button onClick={() => cycle(s.id)} className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-all ${st ? STATUS[st].cls : "bg-white/70 text-nardo-400 border border-nardo-200"}`} data-testid={`asis-toggle-${s.id}`} title="Click para cambiar estado">
                    {st ? React.createElement(STATUS[st].icon, { className: "w-4 h-4" }) : <span className="text-[10px]">?</span>}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-navy font-medium truncate">{s.full_name}</div>
                    <div className="text-[11px] text-nardo-400">{s.grade}</div>
                  </div>
                  <div className="hidden sm:flex items-center gap-1">
                    {ORDER.map((k) => (
                      <button key={k} onClick={() => setStatus(s.id, k)} data-testid={`asis-${k}-${s.id}`} className={`text-[10px] px-2 py-1 rounded-md font-medium transition-all ${st === k ? STATUS[k].cls : "bg-white/60 text-nardo-500 hover:text-navy border border-nardo-200"}`}>{STATUS[k].short}</button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <GroupAttendanceDownload
        title="Descargar asistencia por grupo (Día / Semana / Mes / Año)"
        restrictToGroups
        grades={grades.map((g) => ({ value: g, label: groupLabel(g) }))}
      />


      {/* Nodo imprimible oculto para exportar a imagen (logo + datos del colegio) */}
      <div style={{ position: "fixed", left: -99999, top: 0 }} aria-hidden>
        <div ref={reportRef} style={{ width: 720, background: "#ffffff", padding: 32, fontFamily: "Manrope, Arial, sans-serif", color: "#112240" }} data-testid="asis-report-node">
          <div style={{ display: "flex", alignItems: "center", gap: 14, borderBottom: "3px solid #112240", paddingBottom: 14, marginBottom: 16 }}>
            {logoSrc
              ? <img src={logoSrc} alt="" crossOrigin="anonymous" style={{ height: 54, width: "auto", objectFit: "contain" }} />
              : <img src="/zavia-icon.png" alt="ZAVIA" style={{ width: 46, height: 46, objectFit: "contain" }} />}
            <div>
              <div style={{ fontSize: 12, letterSpacing: 3, textTransform: "uppercase", color: "#1e3a8a" }}>ZAVIA</div>
              <div style={{ fontSize: 22, fontWeight: 800 }}>{profile?.school_name || "Colegio"}</div>
            </div>
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Reporte de asistencia · Grupo {groupLabel(grade)}</div>
          <div style={{ fontSize: 13, color: "#334155", marginBottom: 4 }}>Docente: <b>{profile?.name || "—"}</b>{profile?.subject ? ` · ${profile.subject}` : ""}</div>
          <div style={{ fontSize: 13, color: "#334155", marginBottom: 14 }}>Grupo: <b>{groupLabel(grade)}</b> &nbsp;&nbsp; Fecha: <b>{dateLabel}</b></div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            {ORDER.map((k) => (
              <span key={k} style={{ fontSize: 12, fontWeight: 700, color: "#fff", padding: "4px 12px", borderRadius: 999, background: { presente: "#10b981", ausente: "#ef4444", retardo: "#f59e0b", justificado: "#0ea5e9" }[k] }}>
                {STATUS[k].label}: {counts[k] || 0}
              </span>
            ))}
            <span style={{ fontSize: 12, fontWeight: 700, color: "#112240", padding: "4px 12px", borderRadius: 999, background: "#e2e8f0" }}>Total: {students.length}</span>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#112240", color: "#fff" }}>
                <th style={{ textAlign: "center", padding: "8px 6px", width: 40 }}>#</th>
                <th style={{ textAlign: "left", padding: "8px 10px" }}>Alumno</th>
                <th style={{ textAlign: "left", padding: "8px 10px", width: 130 }}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s, i) => {
                const st = records[s.id];
                const color = { presente: "#059669", ausente: "#dc2626", retardo: "#d97706", justificado: "#0284c7" }[st] || "#94a3b8";
                return (
                  <tr key={s.id} style={{ background: i % 2 ? "#f6f7f8" : "#fff" }}>
                    <td style={{ textAlign: "center", padding: "7px 6px", color: "#94a3b8" }}>{i + 1}</td>
                    <td style={{ padding: "7px 10px", fontWeight: 600 }}>{s.full_name}</td>
                    <td style={{ padding: "7px 10px", fontWeight: 700, color }}>{st ? STATUS[st].label : "Sin marcar"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ fontSize: 10, color: "#64748b", marginTop: 14 }}>Asistencia capturada por el docente y compartida con Dirección en tiempo real. Independiente del registro NFC.</div>
        </div>
      </div>
    </div>
  );
}

const PASS = 7.0;
const TERMS = ["t1", "t2", "t3"];
const TERM_LABELS = { t1: "Trim. 1", t2: "Trim. 2", t3: "Trim. 3" };

/* ---------------- Boletas por trimestre ---------------- */
function Boletas({ profile }) {
  const grades = useMemo(() => {
    const assigned = profile?.groups || [];
    if (assigned.length) return assigned;
    return (profile?.all_grades || []).map((g) => g.grade);
  }, [profile]);
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [students, setStudents] = useState([]);
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [boletaStudent, setBoletaStudent] = useState(null);
  const [boleta, setBoleta] = useState(null);
  const [boletaLoading, setBoletaLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const boletaRef = useRef(null);
  const logoSrc = profile?.logo_url ? `${API}${profile.logo_url}` : null;

  useEffect(() => { if (!grade && grades.length) setGrade(grades[0]); }, [grades]); // eslint-disable-line

  const load = async () => {
    if (!grade) return;
    setLoading(true);
    try {
      const { data } = await api.get("/docente/grades", { params: { grade } });
      setStudents(data.students || []);
      setSubject(data.subject || "General");
      const init = {};
      (data.students || []).forEach((s) => { init[s.id] = data.scores?.[s.id] || { t1: null, t2: null, t3: null }; });
      setScores(init);
    } catch { toast.error("No se pudieron cargar las calificaciones"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [grade]); // eslint-disable-line

  const setScore = (sid, t, v) => setScores((r) => ({ ...r, [sid]: { ...(r[sid] || {}), [t]: v === "" ? null : v } }));
  const avgOf = (sid) => {
    const sc = scores[sid] || {};
    const vals = TERMS.map((t) => sc[t]).filter((v) => v !== null && v !== undefined && v !== "").map(Number);
    if (!vals.length) return null;
    return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { grade, scores: {} };
      Object.keys(scores).forEach((sid) => {
        payload.scores[sid] = { t1: scores[sid]?.t1 ?? null, t2: scores[sid]?.t2 ?? null, t3: scores[sid]?.t3 ?? null };
      });
      await api.post("/docente/grades", payload);
      toast.success("Calificaciones guardadas");
    } catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const openBoleta = async (s) => {
    setBoletaStudent(s); setBoleta(null); setBoletaLoading(true);
    try { const { data } = await api.get(`/docente/boleta/${s.id}`); setBoleta(data); }
    catch { toast.error("No se pudo cargar la boleta"); }
    finally { setBoletaLoading(false); }
  };
  const boletaPDF = () => boletaStudent && downloadPdf(`/docente/boleta/${boletaStudent.id}/pdf`, { filename: `boleta_${boletaStudent.full_name}.pdf` });
  const boletaImage = async () => {
    if (!boletaRef.current) return;
    setExporting(true);
    try {
      const png = await toPng(boletaRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#ffffff" });
      const a = document.createElement("a"); a.href = png; a.download = `boleta_${boletaStudent.full_name}.png`; a.click();
      toast.success("Imagen descargada");
    } catch { toast.error("No se pudo generar la imagen"); }
    finally { setExporting(false); }
  };

  const scoreColor = (v) => (v === null || v === undefined || v === "") ? "text-nardo-400" : (Number(v) < PASS ? "text-red-600" : "text-emerald-600");

  return (
    <div className="space-y-5" data-testid="docente-boletas">
      <div className="doc-glass rounded-2xl p-5 border border-white/50 shadow-lg">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Grupo</label>
            <select data-testid="bol-grade" value={grade} onChange={(e) => setGrade(e.target.value)} className="doc-input min-w-[8rem]">
              {grades.length === 0 && <option value="">Sin grupos</option>}
              {grades.map((g) => <option key={g} value={g}>{groupLabel(g)}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Materia</label>
            <div className="doc-input min-w-[10rem] bg-navy/5" data-testid="bol-subject">{subject || "—"}</div>
          </div>
          <div className="flex-1 min-w-[8rem] flex justify-end">
            <button onClick={save} disabled={saving || !grade || students.length === 0} data-testid="bol-save" className="text-xs inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar calificaciones
            </button>
          </div>
        </div>
        <p className="text-[11px] text-nardo-500 mt-3">Escala 0 a 10 · Aprobatoria {PASS} · Capturas tu materia. La boleta del alumno junta todas las materias.</p>
      </div>

      <div className="doc-glass rounded-2xl border border-white/50 shadow-lg overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…</div>
        ) : students.length === 0 ? (
          <div className="py-16 text-center text-nardo-500 text-sm">No hay alumnos en este grupo.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-navy/5 text-navy text-[11px] uppercase tracking-wider">
                  <th className="text-left px-4 py-2.5">Alumno</th>
                  {TERMS.map((t) => <th key={t} className="px-2 py-2.5 w-24">{TERM_LABELS[t]}</th>)}
                  <th className="px-2 py-2.5 w-20">Prom.</th>
                  <th className="px-2 py-2.5 w-24">Boleta</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/40">
                {students.map((s, i) => {
                  const a = avgOf(s.id);
                  return (
                    <tr key={s.id} data-testid={`bol-row-${s.id}`}>
                      <td className="px-4 py-2 text-navy font-medium">{i + 1}. {s.full_name}</td>
                      {TERMS.map((t) => (
                        <td key={t} className="px-2 py-2 text-center">
                          <input type="number" min="0" max="10" step="0.1" value={scores[s.id]?.[t] ?? ""} onChange={(e) => setScore(s.id, t, e.target.value)} data-testid={`bol-${t}-${s.id}`}
                            className={`w-16 text-center border border-nardo-200 rounded-md py-1.5 font-semibold outline-none focus:border-navy ${scoreColor(scores[s.id]?.[t])}`} />
                        </td>
                      ))}
                      <td className={`px-2 py-2 text-center font-bold ${a === null ? "text-nardo-300" : (a < PASS ? "text-red-600" : "text-emerald-600")}`}>{a === null ? "—" : a}</td>
                      <td className="px-2 py-2 text-center">
                        <button onClick={() => openBoleta(s)} data-testid={`bol-view-${s.id}`} className="inline-flex items-center gap-1 text-[11px] text-navy border border-navy/30 hover:bg-navy hover:text-white rounded-lg px-2.5 py-1.5 transition-colors"><Eye className="w-3.5 h-3.5" /> Ver</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal boleta del alumno */}
      {boletaStudent && (
        <div className="fixed inset-0 z-[80] bg-navy-ink/70 flex items-start justify-center overflow-y-auto p-4" onClick={() => setBoletaStudent(null)} data-testid="bol-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-nardo-200">
              <div className="font-display font-bold text-navy">Boleta · {boletaStudent.full_name}</div>
              <div className="flex items-center gap-2">
                <button onClick={boletaPDF} data-testid="bol-pdf" className="text-xs inline-flex items-center gap-1.5 border border-navy text-navy hover:bg-navy hover:text-white px-3 py-1.5 rounded-lg font-semibold transition-colors"><FileText className="w-3.5 h-3.5" /> PDF</button>
                <button onClick={boletaImage} disabled={exporting} data-testid="bol-image" className="text-xs inline-flex items-center gap-1.5 border border-navy text-navy hover:bg-navy hover:text-white px-3 py-1.5 rounded-lg font-semibold disabled:opacity-50 transition-colors">{exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />} Imagen</button>
                <button onClick={() => setBoletaStudent(null)} className="text-nardo-500 hover:text-navy p-1"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="p-5">
              {boletaLoading ? (
                <div className="py-12 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…</div>
              ) : (
                <div ref={boletaRef} style={{ background: "#fff", padding: 24 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14, borderBottom: "3px solid #112240", paddingBottom: 12, marginBottom: 14 }}>
                    {logoSrc ? <img src={logoSrc} alt="" crossOrigin="anonymous" style={{ height: 48, width: "auto", objectFit: "contain" }} />
                      : <img src="/zavia-icon.png" alt="ZAVIA" style={{ width: 44, height: 44, objectFit: "contain" }} />}
                    <div>
                      <div style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: "#1e3a8a" }}>ZAVIA</div>
                      <div style={{ fontSize: 19, fontWeight: 800, color: "#112240" }}>{profile?.school_name || "Colegio"}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: "#112240", marginBottom: 4 }}>Boleta de calificaciones por trimestre</div>
                  <div style={{ fontSize: 13, color: "#334155", marginBottom: 2 }}>Alumno: <b>{boleta?.student?.full_name}</b></div>
                  <div style={{ fontSize: 13, color: "#334155", marginBottom: 12 }}>Grupo: <b>{boleta?.student?.grade || "—"}</b> &nbsp;&nbsp; Promedio general: <b style={{ color: (boleta?.general_avg != null && boleta.general_avg < PASS) ? "#dc2626" : "#059669" }}>{boleta?.general_avg ?? "—"}</b></div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: "#112240", color: "#fff" }}>
                        <th style={{ textAlign: "left", padding: "8px 10px" }}>Materia</th>
                        <th style={{ textAlign: "left", padding: "8px 10px" }}>Docente</th>
                        {TERMS.map((t) => <th key={t} style={{ padding: "8px 6px", width: 70 }}>{TERM_LABELS[t]}</th>)}
                        <th style={{ padding: "8px 6px", width: 80 }}>Promedio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(boleta?.subjects || []).length === 0 ? (
                        <tr><td colSpan={6} style={{ padding: 14, textAlign: "center", color: "#94a3b8" }}>Aún no hay calificaciones capturadas.</td></tr>
                      ) : (boleta?.subjects || []).map((su, i) => (
                        <tr key={i} style={{ background: i % 2 ? "#f6f7f8" : "#fff" }}>
                          <td style={{ padding: "7px 10px", fontWeight: 600, color: "#112240" }}>{su.subject}</td>
                          <td style={{ padding: "7px 10px", color: "#475569" }}>{su.docente_name}</td>
                          {TERMS.map((t) => {
                            const v = su.terms?.[t];
                            return <td key={t} style={{ padding: "7px 6px", textAlign: "center", fontWeight: 700, color: v == null ? "#94a3b8" : (v < PASS ? "#dc2626" : "#059669") }}>{v == null ? "—" : v}</td>;
                          })}
                          <td style={{ padding: "7px 6px", textAlign: "center", fontWeight: 800, color: su.avg == null ? "#94a3b8" : (su.avg < PASS ? "#dc2626" : "#059669") }}>{su.avg == null ? "—" : su.avg}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ fontSize: 10, color: "#64748b", marginTop: 12 }}>Escala 0 a 10 · Calificación mínima aprobatoria: {PASS} (reprobadas en rojo).</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const INC_TYPES = [
  { id: "academico", label: "Académico", cls: "bg-amber-500" },
  { id: "conductual", label: "Conductual", cls: "bg-indigo-500" },
  { id: "socioemocional", label: "Socio emocional", cls: "bg-teal-500" },
];
const INC_TYPE_LABELS_ALL = {
  academico: { label: "Académico", cls: "bg-amber-500" },
  conductual: { label: "Conductual", cls: "bg-indigo-500" },
  socioemocional: { label: "Socio emocional", cls: "bg-teal-500" },
  conducta: { label: "Conducta negativa", cls: "bg-red-500" },
  reconocimiento: { label: "Reconocimiento positivo", cls: "bg-emerald-500" },
  academica: { label: "Académica", cls: "bg-amber-500" },
  salud: { label: "Salud", cls: "bg-sky-500" },
  otro: { label: "Otro", cls: "bg-slate-500" },
};
const INC_LEVELS = [{ id: "normal", label: "Normal" }, { id: "medio", label: "Medio" }, { id: "grave", label: "Grave" }];
const INC_LEVEL_LABELS = { normal: "Normal", medio: "Medio", grave: "Grave", leve: "Leve", media: "Media" };
const incTypeInfo = (t) => INC_TYPE_LABELS_ALL[t] || INC_TYPE_LABELS_ALL.otro;
const incLevelLabel = (v) => INC_LEVEL_LABELS[v] || (v ? String(v) : "—");

export function AnalysisModal({ data, onClose }) {
  if (!data) return null;
  return (
    <div className="fixed inset-0 z-[85] bg-navy-ink/70 flex items-start justify-center overflow-y-auto p-4" onClick={onClose} data-testid="analysis-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-nardo-200">
          <div className="font-display font-bold text-navy flex items-center gap-2"><Sparkles className="w-4 h-4 text-navy-light" /> Análisis del alumno · {data.name}</div>
          <button onClick={onClose} className="text-nardo-500 hover:text-navy p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">
          {data.loading ? (
            <div className="py-12 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Generando análisis con IA…</div>
          ) : (
            <div className="text-sm text-navy whitespace-pre-wrap leading-relaxed" data-testid="analysis-text">{data.text}</div>
          )}
          <div className="text-[10px] text-nardo-400 mt-4">Generado por IA a partir de incidencias, asistencia y calificaciones. Revisa antes de compartir.</div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Incidencias / Bitácora ---------------- */
function Incidencias({ profile }) {
  const grades = useMemo(() => {
    const assigned = profile?.groups || [];
    if (assigned.length) return assigned;
    return (profile?.all_grades || []).map((g) => g.grade);
  }, [profile]);
  const [grade, setGrade] = useState("");
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [type, setType] = useState("conductual");
  const [level, setLevel] = useState("normal");
  const [desc, setDesc] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { if (!grade && grades.length) setGrade(grades[0]); }, [grades]); // eslint-disable-line
  const load = async () => { if (!grade) return; setLoading(true); try { const { data } = await api.get("/docente/incidents", { params: { grade } }); setItems(data.incidents || []); } catch {} finally { setLoading(false); } };
  useEffect(() => { if (!grade) { setStudents([]); return; } api.get("/docente/students", { params: { grade } }).then((r) => setStudents(r.data.students || [])).catch(() => {}); load(); }, [grade]); // eslint-disable-line

  const submit = async () => {
    if (!studentId) { toast.error("Elige un alumno"); return; }
    if (!desc.trim()) { toast.error("Escribe la descripción"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("student_id", studentId); fd.append("type", type); fd.append("level", level); fd.append("description", desc);
      if (file) fd.append("file", file);
      await api.post("/docente/incidents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Incidencia registrada. Dirección la verá al instante.");
      setDesc(""); setFile(null); if (fileRef.current) fileRef.current.value = "";
      load();
    } catch { toast.error("No se pudo registrar"); }
    finally { setSaving(false); }
  };
  const analyze = async (sid, name) => {
    setAnalysis({ name, text: "", loading: true });
    try { const { data } = await api.post(`/analysis/student/${sid}`); setAnalysis({ name: data.student_name || name, text: data.analysis, loading: false }); }
    catch { setAnalysis(null); toast.error("No se pudo generar el análisis"); }
  };

  return (
    <div className="space-y-5" data-testid="docente-incidencias">
      <div className="doc-glass rounded-2xl p-5 border border-white/50 shadow-lg">
        <h3 className="font-display font-bold text-lg text-navy mb-4 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-navy-light" /> Registrar en bitácora</h3>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Grupo</label>
            <select value={grade} onChange={(e) => setGrade(e.target.value)} data-testid="inc-grade" className="doc-input w-full">
              {grades.length === 0 && <option value="">Sin grupos</option>}
              {grades.map((g) => <option key={g} value={g}>{groupLabel(g)}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Alumno</label>
            <select value={studentId} onChange={(e) => setStudentId(e.target.value)} data-testid="inc-student" className="doc-input w-full">
              <option value="">Selecciona…</option>
              {students.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {INC_TYPES.map((t) => (
            <button key={t.id} onClick={() => setType(t.id)} data-testid={`inc-type-${t.id}`} className={`text-[11px] px-3 py-1.5 rounded-full transition-colors ${type === t.id ? `${t.cls} text-white` : "bg-white/70 text-nardo-600 border border-nardo-200 hover:border-navy"}`}>{t.label}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          <span className="text-[10px] uppercase tracking-[0.2em] text-navy-light self-center mr-1">Nivel:</span>
          {INC_LEVELS.map((l) => (
            <button key={l.id} onClick={() => setLevel(l.id)} data-testid={`inc-level-${l.id}`} className={`text-[11px] px-3 py-1.5 rounded-full transition-colors ${level === l.id ? "bg-navy text-white" : "bg-white/70 text-nardo-600 border border-nardo-200 hover:border-navy"}`}>{l.label}</button>
          ))}
        </div>
        <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} placeholder="Describe lo ocurrido…" data-testid="inc-desc" className="w-full doc-input mb-3 resize-none" />
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-xs inline-flex items-center gap-1.5 text-navy border border-navy/30 hover:bg-navy hover:text-white rounded-lg px-3 py-2 cursor-pointer transition-colors">
            <Camera className="w-4 h-4" /> {file ? "Cambiar foto" : "Adjuntar foto (opcional)"}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="inc-photo" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          {file && <span className="text-[11px] text-nardo-500 truncate max-w-[10rem]">{file.name}</span>}
          <button onClick={submit} disabled={saving} data-testid="inc-submit" className="ml-auto text-xs inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Registrar
          </button>
        </div>
      </div>

      <div className="doc-glass rounded-2xl border border-white/50 shadow-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-white/40 text-sm font-semibold text-navy">Bitácora del grupo {grade}</div>
        {loading ? (
          <div className="py-12 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…</div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-nardo-500 text-sm">Sin incidencias registradas.</div>
        ) : (
          <div className="divide-y divide-white/40">
            {items.map((it) => {
              const ti = incTypeInfo(it.type);
              return (
                <div key={it.id} className="p-4" data-testid={`inc-item-${it.id}`}>
                  <div className="flex items-start gap-3">
                    <span className={`shrink-0 text-[10px] font-semibold text-white px-2 py-1 rounded-full ${ti.cls}`}>{ti.label}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-navy font-semibold">{it.student_name} <span className="text-[11px] text-nardo-400 font-normal">· {incLevelLabel(it.level)} · {(it.created_at || "").slice(0, 10)}</span></div>
                      <div className="text-sm text-nardo-600 mt-0.5">{it.description}</div>
                      {it.photo_token && <a href={`${API}/incident-photo/${it.photo_token}`} target="_blank" rel="noreferrer" className="text-[11px] text-navy-light underline mt-1 inline-block">Ver foto</a>}
                      <div className="text-[11px] text-nardo-400 mt-1">Por {it.created_by_name} · {it.status === "atendida" ? "Atendida por Dirección" : "Pendiente"}</div>
                    </div>
                    <button onClick={() => analyze(it.student_id, it.student_name)} data-testid={`inc-analyze-${it.id}`} className="shrink-0 text-[11px] inline-flex items-center gap-1 text-navy border border-navy/30 hover:bg-navy hover:text-white rounded-lg px-2.5 py-1.5 transition-colors"><Sparkles className="w-3.5 h-3.5" /> Análisis IA</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <AnalysisModal data={analysis} onClose={() => setAnalysis(null)} />
    </div>
  );
}

/* ---------------- Aula Virtual (tipo Google Classroom) ---------------- */
const AULA_TYPES = [
  { id: "tarea", label: "Tarea", icon: ClipboardCheck, badge: "bg-navy text-white", chip: "bg-navy/10 text-navy" },
  { id: "anuncio", label: "Aviso", icon: Megaphone, badge: "bg-amber-500 text-white", chip: "bg-amber-100 text-amber-700" },
  { id: "material", label: "Material", icon: FolderOpen, badge: "bg-emerald-600 text-white", chip: "bg-emerald-100 text-emerald-700" },
];
const aulaTypeInfo = (t) => AULA_TYPES.find((x) => x.id === t) || AULA_TYPES[0];
const ytId = (u) => { const m = (u || "").match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/); return m ? m[1] : null; };
const toLocalInput = (iso) => { if (!iso) return ""; const d = new Date(iso); if (isNaN(d)) return ""; const off = d.getTimezoneOffset(); return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16); };
const fmtDue = (iso) => { if (!iso) return null; const d = new Date(iso); if (isNaN(d)) return null; return d.toLocaleString("es-MX", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }); };

function AulaForm({ group, initial, onSaved, onCancel }) {
  const [type, setType] = useState(initial?.type || "tarea");
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [dueAt, setDueAt] = useState(toLocalInput(initial?.due_at));
  const [attachments, setAttachments] = useState(initial?.attachments || []);
  const [links, setLinks] = useState(initial?.links?.length ? initial.links : [""]);
  const [youtube, setYoutube] = useState(initial?.youtube?.length ? initial.youtube : [""]);
  const [target, setTarget] = useState(initial?.student_ids?.length ? "alumno" : "grupo");
  const [studentIds, setStudentIds] = useState(initial?.student_ids || []);
  const [groupStudents, setGroupStudents] = useState([]);
  useEffect(() => { api.get("/docente/students", { params: { grade: group } }).then((r) => setGroupStudents(r.data.students || [])).catch(() => {}); }, [group]);
  const toggleStudent = (id) => setStudentIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const [uploading, setUploading] = useState(false);
  const [upProgress, setUpProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  // Rúbrica + enlace al Libro de Calificaciones (punto 2a)
  const [rubric, setRubric] = useState(initial?.rubric?.length ? initial.rubric : []);
  const [gbTerm, setGbTerm] = useState(initial?.gb_term || "");
  const [gbCriterion, setGbCriterion] = useState(initial?.gb_criterion || "");
  const [gbCriteria, setGbCriteria] = useState([]);
  useEffect(() => {
    if (!gbTerm || !group) { setGbCriteria([]); return; }
    api.get("/docente/gradebook", { params: { grade: group, term: gbTerm } })
      .then((r) => setGbCriteria(r.data.criteria || [])).catch(() => setGbCriteria([]));
  }, [gbTerm, group]);
  const addRubric = () => setRubric((r) => [...r, { id: `rb_${Math.random().toString(36).slice(2, 8)}`, label: "", max: 10 }]);
  const setRubricRow = (i, k, v) => setRubric((r) => r.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const delRubric = (i) => setRubric((r) => r.filter((_, j) => j !== i));

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        setUpProgress(0);
        const data = await uploadFileChunked(f, { onProgress: setUpProgress });
        setAttachments((a) => [...a, data]);
      }
      toast.success("Archivo(s) adjuntado(s)");
    } catch { toast.error("No se pudo subir el archivo"); }
    finally { setUploading(false); setUpProgress(0); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    if (!title.trim()) { toast.error("Escribe un título"); return; }
    setSaving(true);
    try {
      const payload = {
        group, type, title: title.trim(), description: description.trim(),
        due_at: type === "tarea" && dueAt ? new Date(dueAt).toISOString() : null,
        attachments,
        links: links.map((l) => l.trim()).filter(Boolean),
        youtube: youtube.map((y) => y.trim()).filter(Boolean),
        student_ids: target === "alumno" ? studentIds : [],
        rubric: type === "tarea" ? rubric.filter((c) => (c.label || "").trim()) : [],
        gb_term: type === "tarea" ? (gbTerm || null) : null,
        gb_criterion: type === "tarea" && gbTerm ? (gbCriterion || null) : null,
      };
      if (target === "alumno" && studentIds.length === 0) { toast.error("Selecciona al menos un alumno"); setSaving(false); return; }
      if (initial?.id) await api.put(`/aula/posts/${initial.id}`, payload);
      else await api.post("/aula/posts", payload);
      toast.success(initial?.id ? "Publicación actualizada" : "Publicación creada · los padres fueron notificados");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const setArr = (setter) => (i, v) => setter((arr) => arr.map((x, j) => (j === i ? v : x)));
  const addRow = (setter) => () => setter((arr) => [...arr, ""]);
  const delRow = (setter) => (i) => setter((arr) => arr.filter((_, j) => j !== i));

  return (
    <div className="fixed inset-0 z-[80] bg-navy-ink/70 flex items-start justify-center overflow-y-auto p-4" onClick={onCancel} data-testid="aula-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-nardo-200">
          <div className="font-display font-bold text-navy flex items-center gap-2"><Presentation className="w-4 h-4 text-navy-light" /> {initial?.id ? "Editar publicación" : "Nueva publicación"} · {groupLabel(group)}</div>
          <button onClick={onCancel} className="text-nardo-500 hover:text-navy p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex flex-wrap gap-2">
            {AULA_TYPES.map((t) => (
              <button key={t.id} onClick={() => setType(t.id)} data-testid={`aula-type-${t.id}`}
                className={`text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium transition-colors ${type === t.id ? t.badge : "bg-white text-nardo-600 border border-nardo-200 hover:border-navy"}`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título" data-testid="aula-title" className="w-full border border-nardo-200 rounded-lg px-3 py-2.5 text-sm text-navy outline-none focus:border-navy" />

          <div className="bg-nardo-50 rounded-xl p-3 border border-nardo-200">
            <div className="text-[10px] uppercase tracking-[0.2em] text-navy-light mb-2">¿Para quién es?</div>
            <div className="inline-flex rounded-lg bg-white border border-nardo-200 p-1 mb-1">
              <button type="button" onClick={() => setTarget("grupo")} data-testid="aula-target-grupo" className={`px-4 py-1.5 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 ${target === "grupo" ? "bg-navy text-white" : "text-nardo-600"}`}><Users className="w-3.5 h-3.5" /> Grupo</button>
              <button type="button" onClick={() => setTarget("alumno")} data-testid="aula-target-alumno" className={`px-4 py-1.5 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 ${target === "alumno" ? "bg-navy text-white" : "text-nardo-600"}`}><GraduationCap className="w-3.5 h-3.5" /> Alumno</button>
            </div>
            {target === "grupo" ? (
              <p className="text-[11px] text-nardo-500">Se enviará a todo el grupo <b>{groupLabel(group)}</b>.</p>
            ) : (
              <div>
                <p className="text-[11px] text-nardo-500 mb-2">Selecciona uno o varios alumnos ({studentIds.length} seleccionados):</p>
                <div className="max-h-40 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-1" data-testid="aula-student-list">
                  {groupStudents.length === 0 && <div className="text-[11px] text-nardo-400 py-2">Sin alumnos en este grupo.</div>}
                  {groupStudents.map((s) => (
                    <label key={s.id} data-testid={`aula-student-${s.id}`} className={`flex items-center gap-2 text-xs rounded-lg px-2.5 py-1.5 cursor-pointer border ${studentIds.includes(s.id) ? "bg-navy/5 border-navy" : "bg-white border-nardo-200 hover:border-navy"}`}>
                      <input type="checkbox" checked={studentIds.includes(s.id)} onChange={() => toggleStudent(s.id)} className="accent-navy" />
                      <span className="truncate text-navy">{s.full_name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder="Instrucciones o descripción…" data-testid="aula-desc" className="w-full border border-nardo-200 rounded-lg px-3 py-2.5 text-sm text-navy outline-none focus:border-navy resize-none" />
          {type === "tarea" && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light flex items-center gap-1 mb-1"><CalendarClock className="w-3.5 h-3.5" /> Fecha y hora de entrega (opcional)</label>
              <input type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} data-testid="aula-due" className="border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-navy" />
            </div>
          )}

          {type === "tarea" && (
            <div className="bg-navy/5 rounded-xl p-3 border border-navy/10 space-y-3" data-testid="aula-rubric-section">
              <div className="text-[10px] uppercase tracking-[0.2em] text-navy-light flex items-center gap-1"><Calculator className="w-3.5 h-3.5" /> Rúbrica de evaluación (opcional)</div>
              {rubric.map((c, i) => (
                <div key={c.id} className="flex items-center gap-2" data-testid={`aula-rubric-row-${i}`}>
                  <input value={c.label} onChange={(e) => setRubricRow(i, "label", e.target.value)} data-testid={`aula-rubric-label-${i}`} placeholder="Criterio (ej. Contenido)" className="flex-1 border border-nardo-200 rounded-lg px-2.5 py-1.5 text-xs text-navy outline-none focus:border-navy" />
                  <div className="relative w-24">
                    <input type="number" min="1" max="100" value={c.max} onChange={(e) => setRubricRow(i, "max", e.target.value)} data-testid={`aula-rubric-max-${i}`} className="w-full border border-nardo-200 rounded-lg px-2.5 py-1.5 pr-8 text-xs text-navy text-center outline-none focus:border-navy" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-nardo-400">pts</span>
                  </div>
                  <button type="button" onClick={() => delRubric(i)} className="text-nardo-400 hover:text-red-600 p-1"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <button type="button" onClick={addRubric} data-testid="aula-rubric-add" className="text-[11px] text-navy-light hover:text-navy inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Agregar criterio de rúbrica</button>

              <div className="pt-2 border-t border-navy/10">
                <div className="text-[10px] uppercase tracking-[0.2em] text-navy-light flex items-center gap-1 mb-1.5"><BookOpen className="w-3.5 h-3.5" /> Enviar la nota al Libro de Calificaciones (opcional)</div>
                <div className="flex flex-wrap gap-2">
                  <select value={gbTerm} onChange={(e) => { setGbTerm(e.target.value); setGbCriterion(""); }} data-testid="aula-gb-term" className="border border-nardo-200 rounded-lg px-2.5 py-1.5 text-xs text-navy outline-none focus:border-navy">
                    <option value="">No enviar</option>
                    <option value="t1">Trim. 1</option>
                    <option value="t2">Trim. 2</option>
                    <option value="t3">Trim. 3</option>
                  </select>
                  {gbTerm && (
                    <select value={gbCriterion} onChange={(e) => setGbCriterion(e.target.value)} data-testid="aula-gb-criterion" className="border border-nardo-200 rounded-lg px-2.5 py-1.5 text-xs text-navy outline-none focus:border-navy">
                      <option value="">Elige criterio…</option>
                      {gbCriteria.map((c) => <option key={c.id} value={c.id}>{c.label} ({c.weight}%)</option>)}
                    </select>
                  )}
                </div>
                {gbTerm && gbCriterion && <p className="text-[10px] text-nardo-500 mt-1.5">Al calificar esta tarea, la nota se promediará con las demás tareas del criterio y actualizará sola el libro y la boleta.</p>}
                {gbTerm && gbCriteria.length === 0 && <p className="text-[10px] text-amber-600 mt-1.5">Aún no defines criterios para este trimestre. Créalos en "Calificaciones".</p>}
              </div>
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-navy-light flex items-center gap-1 mb-1.5"><Paperclip className="w-3.5 h-3.5" /> Archivos adjuntos</div>
            <div className="flex flex-wrap gap-2 mb-2">
              {attachments.map((a, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 text-[11px] bg-navy/5 text-navy rounded-lg px-2.5 py-1.5" data-testid={`aula-att-${i}`}>
                  <FileText className="w-3.5 h-3.5" /> <span className="truncate max-w-[9rem]">{a.name}</span>
                  <button onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))} className="text-nardo-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
            <label className="text-xs inline-flex items-center gap-1.5 text-navy border border-navy/30 hover:bg-navy hover:text-white rounded-lg px-3 py-2 cursor-pointer transition-colors">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {uploading ? `Subiendo… ${upProgress}%` : "Adjuntar archivo"}
              <input ref={fileRef} type="file" multiple className="hidden" data-testid="aula-file" onChange={(e) => upload(Array.from(e.target.files || []))} />
            </label>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-navy-light flex items-center gap-1 mb-1.5"><Link2 className="w-3.5 h-3.5" /> Enlaces</div>
              {links.map((l, i) => (
                <div key={i} className="flex items-center gap-1 mb-1.5">
                  <input value={l} onChange={(e) => setArr(setLinks)(i, e.target.value)} placeholder="https://…" data-testid={`aula-link-${i}`} className="flex-1 border border-nardo-200 rounded-lg px-2.5 py-1.5 text-xs text-navy outline-none focus:border-navy" />
                  {links.length > 1 && <button onClick={() => delRow(setLinks)(i)} className="text-nardo-400 hover:text-red-600 p-1"><X className="w-4 h-4" /></button>}
                </div>
              ))}
              <button onClick={addRow(setLinks)} className="text-[11px] text-navy-light hover:text-navy inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Agregar enlace</button>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-navy-light flex items-center gap-1 mb-1.5"><Youtube className="w-3.5 h-3.5" /> Videos de YouTube</div>
              {youtube.map((y, i) => (
                <div key={i} className="flex items-center gap-1 mb-1.5">
                  <input value={y} onChange={(e) => setArr(setYoutube)(i, e.target.value)} placeholder="https://youtu.be/…" data-testid={`aula-yt-${i}`} className="flex-1 border border-nardo-200 rounded-lg px-2.5 py-1.5 text-xs text-navy outline-none focus:border-navy" />
                  {youtube.length > 1 && <button onClick={() => delRow(setYoutube)(i)} className="text-nardo-400 hover:text-red-600 p-1"><X className="w-4 h-4" /></button>}
                </div>
              ))}
              <button onClick={addRow(setYoutube)} className="text-[11px] text-navy-light hover:text-navy inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Agregar video</button>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-nardo-200">
          <button onClick={onCancel} className="text-xs text-nardo-600 hover:text-navy px-3 py-2">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="aula-save" className="text-xs inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {initial?.id ? "Guardar cambios" : "Publicar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AulaPostCard({ post, onEdit, onDelete, onGrade, onViews }) {
  const ti = aulaTypeInfo(post.type);
  const due = fmtDue(post.due_at);
  return (
    <div className="doc-glass rounded-2xl border border-white/50 shadow-lg p-5" data-testid={`aula-post-${post.id}`}>
      <div className="flex items-start gap-3">
        <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-2.5 py-1 rounded-full ${ti.badge}`}><ti.icon className="w-3 h-3" /> {ti.label}</span>
        <div className="flex-1 min-w-0">
          <h4 className="font-display font-bold text-navy text-base leading-tight">{post.title}</h4>
          <div className="text-[11px] text-nardo-400 mt-0.5">{post.docente_name} · {(post.created_at || "").slice(0, 10)}</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onEdit(post)} data-testid={`aula-edit-${post.id}`} className="text-nardo-400 hover:text-navy p-1.5 rounded-lg hover:bg-navy/5"><Pencil className="w-4 h-4" /></button>
          <button onClick={() => onDelete(post)} data-testid={`aula-delete-${post.id}`} className="text-nardo-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      {post.description && <p className="text-sm text-nardo-600 mt-3 whitespace-pre-wrap leading-relaxed">{post.description}</p>}
      {due && <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1"><CalendarClock className="w-3.5 h-3.5" /> Entrega: {due}</div>}

      {post.attachments?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {post.attachments.map((a, i) => (
            <a key={i} href={`${API}/aula/archivo/${a.token}`} target="_blank" rel="noreferrer" data-testid={`aula-post-att-${post.id}-${i}`}
              className="inline-flex items-center gap-1.5 text-[11px] bg-navy/5 hover:bg-navy/10 text-navy rounded-lg px-2.5 py-1.5 transition-colors">
              <FileText className="w-3.5 h-3.5" /> <span className="truncate max-w-[10rem]">{a.name}</span> <Download className="w-3 h-3 text-nardo-400" />
            </a>
          ))}
        </div>
      )}
      {post.links?.length > 0 && (
        <div className="flex flex-col gap-1 mt-3">
          {post.links.map((l, i) => (
            <a key={i} href={l} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-navy-light hover:text-navy underline break-all"><ExternalLink className="w-3.5 h-3.5 shrink-0" /> {l}</a>
          ))}
        </div>
      )}
      {post.youtube?.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          {post.youtube.map((y, i) => {
            const id = ytId(y);
            return id ? (
              <div key={i} className="aspect-video rounded-xl overflow-hidden bg-black/5">
                <iframe title={`yt-${i}`} src={`https://www.youtube.com/embed/${id}`} className="w-full h-full" allowFullScreen frameBorder="0" />
              </div>
            ) : (
              <a key={i} href={y} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:underline"><Youtube className="w-4 h-4" /> {y}</a>
            );
          })}
        </div>
      )}
      {post.type === "tarea" ? (
        post.counts && (
          <div className="mt-4 pt-3 border-t border-white/40 flex flex-wrap items-center gap-3 text-[11px]">
            <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="w-3.5 h-3.5" /> Entregadas: {post.counts.delivered}</span>
            <span className="inline-flex items-center gap-1 text-amber-700"><Clock3 className="w-3.5 h-3.5" /> Pendientes: {post.counts.pending}</span>
            <span className="inline-flex items-center gap-1 text-nardo-500"><Users className="w-3.5 h-3.5" /> Alumnos: {post.counts.total_students}</span>
            <button onClick={() => onViews(post)} data-testid={`aula-views-open-${post.id}`} className="inline-flex items-center gap-1 text-navy hover:underline font-medium">
              <Eye className="w-3.5 h-3.5" /> Vistos: {post.views?.seen ?? 0}/{post.views?.total ?? 0}
            </button>
            <button onClick={() => onGrade(post)} data-testid={`aula-grade-open-${post.id}`} className="ml-auto text-[11px] inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white rounded-lg px-3 py-1.5 font-semibold transition-colors">
              <Award className="w-3.5 h-3.5" /> Ver entregas y calificar
            </button>
          </div>
        )
      ) : (
        <div className="mt-4 pt-3 border-t border-white/40 flex flex-wrap items-center gap-3 text-[11px]">
          <span className="inline-flex items-center gap-1 text-nardo-500"><Users className="w-3.5 h-3.5" /> Familias: {post.views?.total ?? 0}</span>
          <button onClick={() => onViews(post)} data-testid={`aula-views-open-${post.id}`} className="ml-auto text-[11px] inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white rounded-lg px-3 py-1.5 font-semibold transition-colors">
            <Eye className="w-3.5 h-3.5" /> Ver quién lo vio · {post.views?.seen ?? 0}/{post.views?.total ?? 0}
          </button>
        </div>
      )}
    </div>
  );
}

const SUB_STATE_D = {
  pendiente: { label: "Pendiente", cls: "bg-nardo-100 text-nardo-500" },
  entregado: { label: "Entregada", cls: "bg-emerald-100 text-emerald-700" },
  revisado: { label: "Revisada", cls: "bg-sky-100 text-sky-700" },
  calificado: { label: "Calificada", cls: "bg-navy/10 text-navy" },
};

function AulaGradeRow({ post, row, onSaved }) {
  const rubric = post.rubric || [];
  const hasRubric = rubric.length > 0;
  const [grade, setGrade] = useState(row.grade ?? "");
  const [feedback, setFeedback] = useState(row.feedback ?? "");
  const [rScores, setRScores] = useState(() => {
    const init = {};
    rubric.forEach((c) => { init[c.id] = row.rubric_scores?.[c.id] ?? ""; });
    return init;
  });
  const [saving, setSaving] = useState(false);
  const st = SUB_STATE_D[row.status] || SUB_STATE_D.pendiente;

  const rubricGrade = useMemo(() => {
    if (!hasRubric) return null;
    let got = 0, tot = 0;
    rubric.forEach((c) => {
      const mx = Number(c.max) || 0;
      if (mx <= 0) return;
      const v = rScores[c.id];
      if (v === "" || v === null || v === undefined) return;
      got += Math.min(mx, Math.max(0, Number(v))); tot += mx;
    });
    if (tot <= 0) return null;
    return Math.round((got / tot) * 100) / 10;
  }, [rScores, rubric, hasRubric]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { student_id: row.student_id, feedback: feedback.trim() };
      if (hasRubric) {
        const scores = {};
        rubric.forEach((c) => { scores[c.id] = rScores[c.id] === "" ? null : Number(rScores[c.id]); });
        body.rubric_scores = scores;
      } else {
        body.grade = grade === "" ? null : Number(grade);
      }
      const { data } = await api.post(`/aula/posts/${post.id}/grade`, body);
      toast.success(`Calificación guardada · ${row.full_name}`);
      onSaved(row.student_id, { status: data.status, grade: data.grade, feedback: data.feedback, rubric_scores: data.rubric_scores });
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-nardo-200/70 rounded-xl p-3.5" data-testid={`aula-sub-row-${row.student_id}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="text-sm font-semibold text-navy flex-1 min-w-0 truncate">{row.full_name}</div>
        <button onClick={() => downloadPdf(`/aula/tasks-pdf/student/${row.student_id}`, { filename: `boleta_${row.full_name}.pdf` })} data-testid={`aula-student-boleta-${row.student_id}`} title="Boleta de tareas (PDF)" className="text-nardo-400 hover:text-navy p-1 rounded-lg hover:bg-navy/5"><FileText className="w-4 h-4" /></button>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
      </div>
      {row.submitted_at && <div className="text-[11px] text-nardo-400 mb-1.5">Entregó: {new Date(row.submitted_at).toLocaleString("es-MX")}</div>}
      {row.files?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {row.files.map((f, i) => (
            <a key={i} href={`${API}/aula/archivo/${f.token}`} target="_blank" rel="noreferrer" data-testid={`aula-sub-file-${row.student_id}-${i}`} className="inline-flex items-center gap-1.5 text-[11px] bg-navy/5 hover:bg-navy/10 text-navy rounded-lg px-2.5 py-1.5 transition-colors"><FileText className="w-3.5 h-3.5" /> <span className="truncate max-w-[10rem]">{f.name}</span> <Download className="w-3 h-3 text-nardo-400" /></a>
          ))}
        </div>
      )}
      {row.note && <div className="text-[12px] text-nardo-600 bg-nardo-50 rounded-lg px-2.5 py-1.5 mb-2">“{row.note}”</div>}
      {hasRubric && (
        <div className="bg-navy/5 rounded-lg p-2.5 mb-2 space-y-1.5" data-testid={`aula-rubric-grade-${row.student_id}`}>
          <div className="text-[10px] uppercase tracking-[0.15em] text-navy-light">Rúbrica</div>
          {rubric.map((c) => (
            <div key={c.id} className="flex items-center gap-2">
              <span className="text-xs text-navy flex-1 truncate">{c.label}</span>
              <input type="number" min="0" max={c.max} step="0.1" value={rScores[c.id]} onChange={(e) => setRScores((s) => ({ ...s, [c.id]: e.target.value }))} data-testid={`aula-rubric-score-${row.student_id}-${c.id}`} className="w-20 border border-nardo-200 rounded-md px-2 py-1 text-xs text-navy text-center outline-none focus:border-navy" placeholder={`/${c.max}`} />
              <span className="text-[10px] text-nardo-400 w-8">/{c.max}</span>
            </div>
          ))}
          <div className="text-xs font-semibold text-navy text-right pt-1 border-t border-navy/10">Calificación: <span className="text-emerald-600">{rubricGrade === null ? "—" : rubricGrade}</span> / 10</div>
        </div>
      )}
      <div className="flex flex-wrap items-end gap-2">
        {!hasRubric && (
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-navy-light block mb-1">Calificación</label>
            <input type="number" min="0" max="10" step="0.1" value={grade} onChange={(e) => setGrade(e.target.value)} data-testid={`aula-grade-input-${row.student_id}`} className="w-24 border border-nardo-200 rounded-lg px-2.5 py-1.5 text-sm text-navy outline-none focus:border-navy" placeholder="0-10" />
          </div>
        )}
        <input value={feedback} onChange={(e) => setFeedback(e.target.value)} data-testid={`aula-feedback-input-${row.student_id}`} className="flex-1 min-w-[10rem] border border-nardo-200 rounded-lg px-2.5 py-1.5 text-sm text-navy outline-none focus:border-navy" placeholder="Comentario para el alumno/tutor (opcional)" />
        <button onClick={save} disabled={saving} data-testid={`aula-grade-save-${row.student_id}`} className="text-xs inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-3 py-2 rounded-lg font-semibold disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
        </button>
      </div>
    </div>
  );
}

function AulaSubmissionsModal({ post, onClose, onGraded }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/aula/posts/${post.id}/submissions`); setData(data); }
    catch { toast.error("No se pudieron cargar las entregas"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [post.id]); // eslint-disable-line

  const onRowSaved = (studentId, patch) => {
    setData((d) => {
      if (!d) return d;
      const students = d.students.map((r) => (r.student_id === studentId ? { ...r, ...patch } : r));
      const delivered = students.filter((r) => ["entregado", "revisado", "calificado"].includes(r.status)).length;
      const graded = students.filter((r) => r.status === "calificado").length;
      return { ...d, students, counts: { ...d.counts, delivered, graded, pending: students.length - delivered } };
    });
    onGraded();
  };

  return (
    <div className="fixed inset-0 z-[82] bg-navy-ink/70 flex items-start justify-center overflow-y-auto p-4" onClick={onClose} data-testid="aula-submissions-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-nardo-200">
          <div className="font-display font-bold text-navy flex items-center gap-2 min-w-0"><Award className="w-4 h-4 text-navy-light shrink-0" /> <span className="truncate">Entregas · {post.title}</span></div>
          <div className="flex items-center gap-2">
            <button onClick={() => downloadPdf(`/aula/tasks-pdf/group`, { params: { group: post.group }, filename: `boleta_tareas_grupo.pdf` })} data-testid="aula-group-boleta-pdf" className="text-[11px] inline-flex items-center gap-1.5 border border-navy text-navy hover:bg-navy hover:text-white px-2.5 py-1.5 rounded-lg font-semibold transition-colors"><FileText className="w-3.5 h-3.5" /> Boleta del grupo</button>
            <button onClick={onClose} className="text-nardo-500 hover:text-navy p-1"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="p-5">
          {loading ? (
            <div className="py-12 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…</div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-4 text-[11px]">
                <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">Entregadas: {data.counts.delivered}</span>
                <span className="px-2.5 py-1 rounded-full bg-navy/10 text-navy">Calificadas: {data.counts.graded}</span>
                <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">Pendientes: {data.counts.pending}</span>
                <span className="px-2.5 py-1 rounded-full bg-nardo-100 text-nardo-600">Total: {data.counts.total}</span>
              </div>
              <div className="space-y-3">
                {data.students.length === 0 ? (
                  <div className="py-8 text-center text-nardo-500 text-sm">No hay alumnos en este grupo.</div>
                ) : data.students.map((r) => <AulaGradeRow key={r.student_id} post={post} row={r} onSaved={onRowSaved} />)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AulaViewsModal({ post, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const { data } = await api.get(`/aula/posts/${post.id}/views`); setData(data); }
      catch { toast.error("No se pudo cargar quién lo vio"); }
      finally { setLoading(false); }
    })();
  }, [post.id]); // eslint-disable-line
  const fmt = (iso) => { if (!iso) return ""; const d = new Date(iso); return isNaN(d) ? "" : d.toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); };
  return (
    <div className="fixed inset-0 z-[82] bg-navy-ink/70 flex items-start justify-center overflow-y-auto p-4" onClick={onClose} data-testid="aula-views-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-nardo-200">
          <div className="font-display font-bold text-navy flex items-center gap-2 min-w-0"><Eye className="w-4 h-4 text-navy-light shrink-0" /> <span className="truncate">Visto por · {post.title}</span></div>
          <button onClick={onClose} className="text-nardo-500 hover:text-navy p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">
          {loading ? (
            <div className="py-12 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…</div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-4 text-[11px]">
                <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">Vistas: {data.counts.seen}</span>
                <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">Sin ver: {data.counts.pending}</span>
                <span className="px-2.5 py-1 rounded-full bg-nardo-100 text-nardo-600">Total: {data.counts.total}</span>
              </div>
              <div className="divide-y divide-nardo-100">
                {data.students.length === 0 ? (
                  <div className="py-8 text-center text-nardo-500 text-sm">No hay alumnos en este grupo.</div>
                ) : data.students.map((r) => (
                  <div key={r.student_id} className="flex items-center justify-between gap-2 py-2.5 text-sm" data-testid={`aula-view-row-${r.student_id}`}>
                    <span className="text-navy truncate">{r.full_name}</span>
                    {r.seen ? (
                      <span className="shrink-0 inline-flex items-center gap-1.5 text-emerald-700 font-medium"><CheckCircle2 className="w-4 h-4" /> Visto {r.seen_at ? <span className="text-[11px] text-nardo-400 font-normal">· {fmt(r.seen_at)}</span> : null}</span>
                    ) : (
                      <span className="shrink-0 inline-flex items-center gap-1.5 text-nardo-400"><Clock className="w-4 h-4" /> Sin ver</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


/* ---------------- Planeaciones (NEM) ---------------- */
const CAMPOS = ["Lenguajes", "Saberes y pensamiento científico", "Ética, naturaleza y sociedades", "De lo humano y lo comunitario"];
const METODOLOGIAS = ["ABPP · Proyectos comunitarios", "ABP · Basado en problemas", "ABI · Indagación (STEAM)", "AS · Aprendizaje-servicio"];
const EJES = ["Inclusión", "Pensamiento crítico", "Interculturalidad crítica", "Igualdad de género", "Vida saludable", "Lectura y escritura", "Artes y experiencias estéticas"];
const ESCENARIOS = ["Aula", "Escolar", "Comunitario"];
const faseFor = (group) => {
  const parts = (group || "").split("|"); const g = parts[0] || ""; const L = (parts[1] || "").toLowerCase();
  const n = parseInt(g, 10) || 0;
  if (L.includes("prees")) return "Fase 2 · Preescolar";
  if (L.includes("secund")) return "Fase 6 · Secundaria";
  if (L.includes("prim")) { if ([1, 2].includes(n)) return "Fase 3 · 1° y 2° de Primaria"; if ([3, 4].includes(n)) return "Fase 4 · 3° y 4° de Primaria"; if ([5, 6].includes(n)) return "Fase 5 · 5° y 6° de Primaria"; return "Primaria"; }
  return "";
};

function PlaneacionForm({ initial, groups, onSaved, onCancel }) {
  const [f, setF] = useState({
    proyecto: "", group: groups[0] || "", campo_formativo: CAMPOS[0], metodologia: METODOLOGIAS[0],
    ejes: [], temporalidad: "", escenario: ESCENARIOS[0], proposito: "", contenidos: "", pda: "",
    problematica: "", fase1: "", fase2: "", fase3: "", recursos: "", evaluacion: "", observaciones: "",
    ...(initial || {}),
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const toggleEje = (e) => setF((s) => ({ ...s, ejes: s.ejes.includes(e) ? s.ejes.filter((x) => x !== e) : [...s.ejes, e] }));
  const fase = faseFor(f.group);
  const save = async () => {
    if (!f.proyecto.trim()) { toast.error("Escribe el nombre del proyecto"); return; }
    setSaving(true);
    try {
      const body = { ...f, fase };
      if (initial?.id) await api.put(`/docente/planeaciones/${initial.id}`, body);
      else await api.post("/docente/planeaciones", body);
      toast.success(initial?.id ? "Planeación actualizada" : "Planeación creada");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };
  const Field = ({ label, k, rows = 3, ph }) => (
    <div><label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">{label}</label>
      <textarea value={f[k]} onChange={set(k)} rows={rows} placeholder={ph} data-testid={`plan-${k}`} className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-navy resize-none" /></div>
  );
  return (
    <div className="fixed inset-0 z-[80] bg-navy-ink/70 flex items-start justify-center overflow-y-auto p-4" onClick={onCancel} data-testid="plan-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-nardo-200">
          <div className="font-display font-bold text-navy flex items-center gap-2"><FileText className="w-4 h-4 text-navy-light" /> {initial?.id ? "Editar planeación" : "Nueva planeación"}</div>
          <button onClick={onCancel} className="text-nardo-500 hover:text-navy p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <input value={f.proyecto} onChange={set("proyecto")} placeholder="Nombre del proyecto" data-testid="plan-proyecto" className="w-full border border-nardo-200 rounded-lg px-3 py-2.5 text-sm text-navy outline-none focus:border-navy font-semibold" />
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Grado y grupo</label>
              <select value={f.group} onChange={set("group")} data-testid="plan-group" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-navy bg-white">
                {groups.map((g) => <option key={g} value={g}>{groupLabel(g)}</option>)}
              </select></div>
            <div><label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Fase (automática)</label>
              <div data-testid="plan-fase" className="w-full border border-emerald-200 bg-emerald-50 text-emerald-800 rounded-lg px-3 py-2 text-sm font-semibold">{fase || "—"}</div></div>
            <div><label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Campo formativo</label>
              <select value={f.campo_formativo} onChange={set("campo_formativo")} data-testid="plan-campo" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-navy bg-white">
                {CAMPOS.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
            <div><label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Metodología</label>
              <select value={f.metodologia} onChange={set("metodologia")} data-testid="plan-metodologia" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-navy bg-white">
                {METODOLOGIAS.map((m) => <option key={m} value={m}>{m}</option>)}</select></div>
            <div><label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Temporalidad</label>
              <input value={f.temporalidad} onChange={set("temporalidad")} placeholder="Ej. 2 semanas · 10 al 21 nov" data-testid="plan-temporalidad" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-navy" /></div>
            <div><label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Escenario</label>
              <select value={f.escenario} onChange={set("escenario")} data-testid="plan-escenario" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-navy bg-white">
                {ESCENARIOS.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
          </div>
          <div><label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Ejes articuladores</label>
            <div className="flex flex-wrap gap-1.5">
              {EJES.map((e) => <button type="button" key={e} onClick={() => toggleEje(e)} data-testid={`plan-eje-${e}`} className={`text-[11px] px-2.5 py-1 rounded-full border ${f.ejes.includes(e) ? "bg-navy text-white border-navy" : "bg-white text-nardo-600 border-nardo-200"}`}>{e}</button>)}
            </div></div>
          <Field label="Propósito / Aprendizaje esperado" k="proposito" ph="¿Qué se espera que logren los alumnos?" />
          <Field label="Contenidos" k="contenidos" rows={2} />
          <Field label="Procesos de Desarrollo de Aprendizaje (PDA)" k="pda" rows={2} />
          <Field label="Problemática / Cuestionamientos detonadores" k="problematica" rows={2} />
          <Field label="Fase 1 · Planeación (identificación, recuperación, planificación)" k="fase1" />
          <Field label="Fase 2 · Acción (acercamiento, comprensión, producción)" k="fase2" />
          <Field label="Fase 3 · Intervención (integración, difusión, cierre)" k="fase3" />
          <Field label="Recursos y materiales" k="recursos" rows={2} />
          <Field label="Evaluación (instrumentos y modalidad)" k="evaluacion" rows={2} />
          <Field label="Observaciones y ajustes razonables" k="observaciones" rows={2} />
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-nardo-200">
          <button onClick={onCancel} className="text-sm px-4 py-2 rounded-lg text-nardo-600 hover:bg-nardo-100">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="plan-save" className="text-sm px-4 py-2 rounded-lg bg-navy hover:bg-navy-light text-white font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar</button>
        </div>
      </div>
    </div>
  );
}

function Planeaciones({ profile }) {
  const groups = useMemo(() => (profile?.groups || []), [profile]);
  const [items, setItems] = useState(null);
  const [shared, setShared] = useState([]);
  const [ingles, setIngles] = useState(null); // null | {} | planeacion (Inglés)
  const [fmt, setFmt] = useState(null); // {tipo, initial} para formatos preescolar/primsec
  const [evalr, setEvalr] = useState(null); // null | {} | planeacion (Reporte de evaluación)
  const [areas, setAreas] = useState(null); // null | {} | planeacion (Plano áreas especiales)
  const [del, setDel] = useState(null);
  const [trash, setTrash] = useState(null); // null=cerrada | [] cargando/vacía | items
  const [trashCount, setTrashCount] = useState(0);
  const load = () => api.get("/docente/planeaciones").then(({ data }) => { setItems(data.planeaciones || []); setShared(data.shared_templates || []); }).catch(() => { setItems([]); setShared([]); });
  const loadTrashCount = () => api.get("/docente/planeaciones/trash").then(({ data }) => setTrashCount((data.planeaciones || []).length)).catch(() => {});
  useEffect(() => { load(); loadTrashCount(); }, []);
  const openTrash = async () => { try { const { data } = await api.get("/docente/planeaciones/trash"); const list = data.planeaciones || []; setTrash(list); setTrashCount(list.length); } catch { toast.error("No se pudo abrir la papelera"); } };
  const restorePlan = async (p) => { try { await api.post(`/docente/planeaciones/${p.id}/restore`); toast.success("Planeación recuperada"); setTrash((t) => (t || []).filter((x) => x.id !== p.id)); setTrashCount((c) => Math.max(0, c - 1)); load(); } catch { toast.error("No se pudo recuperar"); } };
  const purgePlan = async (p) => { try { await api.delete(`/docente/planeaciones/${p.id}/purge`); toast.success("Eliminada definitivamente"); setTrash((t) => (t || []).filter((x) => x.id !== p.id)); setTrashCount((c) => Math.max(0, c - 1)); } catch { toast.error("No se pudo eliminar"); } };
  const remove = async () => { try { await api.delete(`/docente/planeaciones/${del.id}`); toast.success("Movida a la papelera"); setDel(null); load(); loadTrashCount(); } catch { toast.error("No se pudo eliminar"); } };
  const editItem = (p) => { if (p.tipo === "preescolar" || p.tipo === "primsec") setFmt({ tipo: p.tipo, initial: p }); else if (p.tipo === "ingles") setIngles(p); else if (p.tipo === "evaluacion") setEvalr(p); else if (p.tipo === "areas") setAreas(p); };
  const duplicate = async (p) => { try { await api.post(`/docente/planeaciones/${p.id}/duplicate`); toast.success("Planeación duplicada"); load(); } catch { toast.error("No se pudo duplicar"); } };
  const submitReview = async (p) => { try { await api.post(`/docente/planeaciones/${p.id}/submit`); toast.success("Enviada a dirección para revisión"); load(); } catch { toast.error("No se pudo enviar"); } };
  const toggleTemplate = async (p) => { try { await api.post(`/docente/planeaciones/${p.id}/template`, { value: !p.is_template }); toast.success(!p.is_template ? "Guardada como plantilla" : "Ya no es plantilla"); load(); } catch { toast.error("No se pudo actualizar"); } };
  const tipoName = (t) => (t === "preescolar" ? "Preescolar" : t === "primsec" ? "Primaria y Secundaria" : t === "ingles" ? "Inglés" : t === "evaluacion" ? "Reporte de evaluación" : t === "areas" ? "Áreas especiales" : "Proyectos (NEM)");
  const startFromTemplate = (p) => {
    const clonedData = p.data ? JSON.parse(JSON.stringify(p.data)) : {};
    const clone = { proyecto: `${p.proyecto} (nueva)`, tipo: p.tipo, color: p.color, formato: p.formato, data: clonedData };
    if (p.tipo === "preescolar" || p.tipo === "primsec") setFmt({ tipo: p.tipo, initial: clone });
    else if (p.tipo === "ingles") setIngles(clone);
    else if (p.tipo === "evaluacion") setEvalr(clone);
    else if (p.tipo === "areas") setAreas(clone);
  };
  const templates = (items || []).filter((i) => i.is_template && i.tipo !== "nem");
  const sharedPlans = (shared || []).filter((t) => t.tipo !== "nem");

  return (
    <div data-testid="planeaciones-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div><h2 className="font-display font-bold text-lg text-navy flex items-center gap-2"><FileText className="w-5 h-5 text-navy-light" /> Planeaciones</h2>
          <p className="text-xs text-nardo-500">Crea tu planeación didáctica (NEM), elige uno de los 10 formatos de color y descárgala en PDF con el logo de tu colegio. <span className="text-emerald-600 font-medium">Se autoguardan solas cada segundo.</span></p></div>
        <button onClick={openTrash} data-testid="plan-trash-open" className="inline-flex items-center gap-1.5 text-xs font-semibold text-nardo-600 border border-nardo-200 rounded-lg px-3 py-2 hover:bg-nardo-100">
          <Trash2 className="w-4 h-4" /> Papelera{trashCount > 0 ? ` (${trashCount})` : ""}
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-5">
        <button onClick={() => setFmt({ tipo: "preescolar", initial: null })} data-testid="plan-new-preescolar" className="rounded-xl px-4 py-3 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 bg-gradient-to-br from-emerald-500 to-teal-600 hover:opacity-95 shadow"><Plus className="w-4 h-4" /> Planeación de preescolar</button>
        <button onClick={() => setFmt({ tipo: "primsec", initial: null })} data-testid="plan-new-primsec" className="rounded-xl px-4 py-3 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 bg-gradient-to-br from-indigo-500 to-navy hover:opacity-95 shadow"><Plus className="w-4 h-4" /> Planeación primaria y secundaria</button>
        <button onClick={() => setIngles({})} data-testid="plan-new-ingles" className="rounded-xl px-4 py-3 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 bg-gradient-to-br from-green-500 to-emerald-600 hover:opacity-95 shadow"><Plus className="w-4 h-4" /> Planeación de Inglés</button>
        <button onClick={() => setEvalr({})} data-testid="plan-new-evaluacion" className="rounded-xl px-4 py-3 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 bg-gradient-to-br from-rose-500 to-purple-600 hover:opacity-95 shadow"><Plus className="w-4 h-4" /> Reporte de evaluación</button>
        <button onClick={() => setAreas({})} data-testid="plan-new-areas" className="rounded-xl px-4 py-3 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 bg-gradient-to-br from-amber-500 to-yellow-600 hover:opacity-95 shadow"><Plus className="w-4 h-4" /> Plano didáctico áreas especiales</button>
      </div>
      <div className="mb-5">
        <SignatureUploader title="Mi firma para planeaciones" help="Sube tu firma escaneada (PNG con fondo transparente). Aparecerá automáticamente en el PDF de tus planeaciones de Inglés, sobre la línea 'Teacher'." />
      </div>
      {(templates.length > 0 || sharedPlans.length > 0) && (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="plan-templates-bar">
          <div className="text-[11px] uppercase tracking-wide text-amber-700 mb-2 font-semibold flex items-center gap-1.5"><Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" /> Nueva desde plantilla · reutiliza la estructura sin volver a capturar</div>
          <div className="flex flex-wrap gap-2">
            {templates.map((t) => (
              <button key={t.id} onClick={() => startFromTemplate(t)} data-testid={`plan-use-tpl-${t.id}`} title={`Crear una nueva planeación con la estructura de "${t.proyecto}"`}
                className="text-xs inline-flex items-center gap-1.5 bg-white border-2 rounded-lg px-3 py-2 font-semibold hover:shadow-sm transition-shadow" style={{ borderColor: t.color || "#0b2a5b", color: t.color || "#0b2a5b" }}>
                <Plus className="w-3.5 h-3.5" /> {t.proyecto}
                <span className="text-[10px] text-nardo-400 font-normal">· {tipoName(t.tipo)}</span>
              </button>
            ))}
            {sharedPlans.map((t) => (
              <button key={t.id} onClick={() => startFromTemplate(t)} data-testid={`plan-use-shared-${t.id}`} title={`Crear una nueva planeación con la estructura compartida "${t.proyecto}"`}
                className="text-xs inline-flex items-center gap-1.5 bg-white border-2 border-dashed rounded-lg px-3 py-2 font-semibold hover:shadow-sm transition-shadow" style={{ borderColor: t.color || "#0b2a5b", color: t.color || "#0b2a5b" }}>
                <Users className="w-3.5 h-3.5" /> {t.proyecto}
                <span className="text-[10px] text-nardo-400 font-normal">· {tipoName(t.tipo)}{t.docente_name ? ` · de ${t.docente_name}` : " · del colegio"}</span>
              </button>
            ))}
          </div>
          {sharedPlans.length > 0 && <div className="text-[10px] text-amber-700/80 mt-2">Las plantillas con borde punteado fueron compartidas por otros docentes de tu colegio.</div>}
        </div>
      )}
      {items === null ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-navy" /></div> :
        items.length === 0 ? <div className="py-16 text-center text-nardo-500" data-testid="plan-empty">Aún no tienes planeaciones. Elige un formato y crea la primera con los botones de arriba.</div> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((p) => {
              const accent = p.color || "#0b2a5b";
              const tipoLabel = p.tipo === "preescolar" ? "Preescolar" : p.tipo === "primsec" ? "Primaria y Secundaria" : p.tipo === "ingles" ? "Inglés" : p.tipo === "evaluacion" ? "Reporte de evaluación" : p.tipo === "areas" ? "Áreas especiales" : "Proyectos (NEM)";
              return (
              <div key={p.id} className="bg-white rounded-2xl border border-nardo-200 shadow-sm p-4 flex flex-col overflow-hidden relative" data-testid={`plan-card-${p.id}`}>
                <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: accent }} />
                <button onClick={() => toggleTemplate(p)} data-testid={`plan-template-${p.id}`} title={p.is_template ? "Quitar de plantillas" : "Guardar como plantilla"} className="absolute top-2.5 right-2.5">
                  <Star className={`w-4 h-4 ${p.is_template ? "fill-amber-400 text-amber-400" : "text-nardo-300 hover:text-amber-400"}`} />
                </button>
                <div className="font-bold text-navy text-sm leading-tight mt-1 pr-6">{p.proyecto}</div>
                <div className="text-[11px] mt-1 flex flex-wrap items-center gap-1.5">
                  <span className="px-1.5 py-0.5 rounded-full text-white text-[10px] font-semibold" style={{ background: accent }}>{tipoLabel}</span>
                  {p.is_template && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold inline-flex items-center gap-0.5"><Star className="w-2.5 h-2.5 fill-amber-500 text-amber-500" /> Plantilla</span>}
                  {p.review_status === "enviada" && <span className="px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 text-[10px] font-semibold inline-flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" /> En revisión</span>}
                  {p.review_status === "aprobada" && <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold inline-flex items-center gap-0.5"><CheckCircle2 className="w-2.5 h-2.5" /> Aprobada</span>}
                  {p.review_status === "cambios" && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold">Cambios solicitados</span>}
                </div>
                {p.review_status === "cambios" && p.review_note && <div className="text-[11px] text-amber-700 bg-amber-50 rounded-md px-2 py-1 mt-1.5">Dirección: {p.review_note}</div>}
                {p.reviewed_by_name && p.review_status !== "enviada" && <div className="text-[11px] text-indigo-600 mt-1">Revisó: {p.reviewed_by_name}{p.reviewed_by_role ? ` — ${p.reviewed_by_role}` : ""}</div>}
                <div className="text-[11px] text-nardo-400 mt-1">{p.tipo && p.tipo !== "nem" ? (p.data?.grado ? `Grado: ${p.data.grado}` : "") : `${p.group_label} · ${p.fase || "—"}`}</div>
                <div className="flex flex-wrap items-center gap-1.5 mt-3 pt-3 border-t border-nardo-100">
                  <button onClick={async () => { const tid = toast.loading("Generando PDF…"); try { await downloadPdf(`/docente/planeaciones/${p.id}/pdf`, { filename: `planeacion_${p.proyecto}.pdf` }); toast.success("PDF listo", { id: tid }); } catch (e) { toast.error(e?.message || "No se pudo descargar el PDF.", { id: tid }); } }} data-testid={`plan-pdf-${p.id}`} className="text-[11px] inline-flex items-center gap-1 border text-navy hover:text-white px-2.5 py-1.5 rounded-lg font-semibold transition-colors" style={{ borderColor: accent }} onMouseEnter={(e) => { e.currentTarget.style.background = accent; }} onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}><Download className="w-3.5 h-3.5" /> PDF</button>
                  {p.review_status !== "enviada" && p.review_status !== "aprobada" && (
                    <button onClick={() => submitReview(p)} data-testid={`plan-submit-${p.id}`} className="text-[11px] inline-flex items-center gap-1 text-white px-2.5 py-1.5 rounded-lg font-semibold" style={{ background: accent }}><Send className="w-3.5 h-3.5" /> Enviar a dirección</button>
                  )}
                  {(p.tipo === "preescolar" || p.tipo === "primsec" || p.tipo === "ingles" || p.tipo === "evaluacion" || p.tipo === "areas") && <button onClick={() => editItem(p)} data-testid={`plan-edit-${p.id}`} className="text-[11px] inline-flex items-center gap-1 text-nardo-600 hover:text-navy px-2 py-1.5 rounded-lg hover:bg-nardo-100"><Pencil className="w-3.5 h-3.5" /> Editar</button>}
                  <button onClick={() => duplicate(p)} data-testid={`plan-dup-${p.id}`} className="text-[11px] inline-flex items-center gap-1 text-nardo-600 hover:text-navy px-2 py-1.5 rounded-lg hover:bg-nardo-100"><Copy className="w-3.5 h-3.5" /> Duplicar</button>
                  <button onClick={() => setDel(p)} data-testid={`plan-del-${p.id}`} className="ml-auto text-nardo-400 hover:text-red-600 p-1.5"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ); })}
          </div>
        )}
      {ingles && <PlaneacionIngles initial={ingles.id || ingles.proyecto ? ingles : null} defaultDocente={profile?.name || profile?.docente_name || ""} onSaved={() => { setIngles(null); load(); }} onCancel={() => setIngles(null)} />}
      {fmt && <PlaneacionFormato tipo={fmt.tipo} initial={fmt.initial} defaultDocente={profile?.name || profile?.docente_name || ""} onSaved={() => { setFmt(null); load(); }} onCancel={() => setFmt(null)} />}
      {evalr && <PlaneacionEvaluacion initial={evalr.id || evalr.proyecto ? evalr : null} onSaved={() => { setEvalr(null); load(); }} onCancel={() => setEvalr(null)} />}
      {areas && <PlaneacionAreas initial={areas.id || areas.proyecto ? areas : null} onSaved={() => { setAreas(null); load(); }} onCancel={() => setAreas(null)} />}
      {del && (
        <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" onClick={() => setDel(null)}>
          <div className="bg-white rounded-2xl p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="font-bold text-navy mb-2">¿Enviar a la papelera?</div>
            <p className="text-sm text-nardo-500 mb-4">“{del.proyecto}” se moverá a la papelera. Podrás recuperarla cuando quieras.</p>
            <div className="flex justify-end gap-2"><button onClick={() => setDel(null)} className="text-sm px-4 py-2 rounded-lg text-nardo-600 hover:bg-nardo-100">Cancelar</button><button onClick={remove} data-testid="plan-del-confirm" className="text-sm px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold inline-flex items-center gap-1.5"><Trash2 className="w-4 h-4" /> A la papelera</button></div>
          </div>
        </div>
      )}
      {trash !== null && (
        <div className="fixed inset-0 z-[90] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setTrash(null)} data-testid="plan-trash-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-nardo-200">
              <div className="font-display font-bold text-navy flex items-center gap-2"><Trash2 className="w-4 h-4 text-navy-light" /> Papelera de planeaciones</div>
              <button onClick={() => setTrash(null)} className="text-nardo-500 hover:text-navy p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              {trash.length === 0 ? (
                <div className="py-12 text-center text-sm text-nardo-500" data-testid="plan-trash-empty">La papelera está vacía. Aquí aparecen las planeaciones que borres, por si necesitas recuperarlas.</div>
              ) : (
                <div className="space-y-2">
                  {trash.map((p) => {
                    const accent = p.color || "#0b2a5b";
                    const tName = p.tipo === "preescolar" ? "Preescolar" : p.tipo === "primsec" ? "Primaria y Secundaria" : p.tipo === "ingles" ? "Inglés" : p.tipo === "evaluacion" ? "Reporte de evaluación" : p.tipo === "areas" ? "Áreas especiales" : "Proyectos (NEM)";
                    return (
                      <div key={p.id} className="flex items-center gap-3 rounded-xl border border-nardo-200 px-3 py-2.5" data-testid={`plan-trash-item-${p.id}`}>
                        <span className="w-1.5 h-8 rounded-full shrink-0" style={{ background: accent }} />
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-navy text-sm truncate">{p.proyecto}</div>
                          <div className="text-[11px] text-nardo-400">{tName}{p.deleted_at ? ` · eliminada ${new Date(p.deleted_at).toLocaleDateString()}` : ""}</div>
                        </div>
                        <button onClick={() => restorePlan(p)} data-testid={`plan-restore-${p.id}`} className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 border border-emerald-300 rounded-lg px-2.5 py-1.5 hover:bg-emerald-50"><RotateCcw className="w-3.5 h-3.5" /> Recuperar</button>
                        <button onClick={() => purgePlan(p)} data-testid={`plan-purge-${p.id}`} title="Eliminar definitivamente" className="shrink-0 text-nardo-400 hover:text-red-600 p-1.5"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Clubs (docente) ---------------- */
const CLUB_STATUS = [
  { id: "presente", label: "Presente", cls: "bg-emerald-600 text-white", dot: "bg-emerald-500" },
  { id: "retardo", label: "Retardo", cls: "bg-amber-500 text-white", dot: "bg-amber-500" },
  { id: "justificado", label: "Justif.", cls: "bg-slate-500 text-white", dot: "bg-slate-500" },
  { id: "ausente", label: "Ausente", cls: "bg-red-600 text-white", dot: "bg-red-500" },
];
const CLUB_RANGES = [{ id: "day", label: "Día" }, { id: "week", label: "Semana" }, { id: "fortnight", label: "Quincena" }, { id: "month", label: "Mes" }];

/* Comunicados de club: el docente envía info SOLO a los papás de su club */
function ClubComunicadosDocente({ club }) {
  const [items, setItems] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/docente/clubs/${club.id}/announcements`)
    .then(({ data }) => setItems(data.announcements || [])).catch(() => setItems([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [club.id]);

  const submit = async () => {
    if (!title.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("body", body.trim());
      if (file) fd.append("file", file);
      await api.post(`/docente/clubs/${club.id}/announcements`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Comunicado enviado a los papás del club");
      setTitle(""); setBody(""); setFile(null); setShowForm(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo enviar"); }
    finally { setSaving(false); }
  };

  const del = async (a) => {
    if (!window.confirm("¿Eliminar este comunicado?")) return;
    try { await api.delete(`/docente/clubs/${club.id}/announcements/${a.id}`); toast.success("Eliminado"); load(); }
    catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <div className="mt-5 bg-white rounded-2xl border border-nardo-200 shadow-sm p-4" data-testid="club-comunicados">
      <div className="flex items-center gap-2 mb-3">
        <Megaphone className="w-4 h-4 text-navy-light" />
        <div className="font-display font-bold text-navy text-sm">Comunicados a los papás</div>
        <button onClick={() => setShowForm((v) => !v)} data-testid="club-com-toggle" className="ml-auto text-xs inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-3 py-1.5 rounded-lg font-semibold"><Plus className="w-3.5 h-3.5" /> Nuevo</button>
      </div>
      <p className="text-[11px] text-nardo-400 mb-3">Envía información SOLO a los papás de los alumnos de este club. Les llega como notificación.</p>
      {showForm && (
        <div className="border border-nardo-200 rounded-xl p-3 mb-4 space-y-3" data-testid="club-com-form">
          <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="club-com-title" placeholder="Título (ej. Cambio de horario)" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} data-testid="club-com-body" rows={3} placeholder="Escribe la información para los papás…" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy resize-none" />
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-xs inline-flex items-center gap-1.5 text-navy border border-navy/30 hover:bg-navy hover:text-white rounded-lg px-3 py-2 cursor-pointer transition-colors">
              <Paperclip className="w-3.5 h-3.5" /> {file ? "Cambiar adjunto" : "Adjuntar (opcional)"}
              <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="club-com-file" />
            </label>
            {file && <span className="text-[11px] text-nardo-500 truncate max-w-[12rem]">{file.name}</span>}
            <button onClick={submit} disabled={saving} data-testid="club-com-send" className="ml-auto text-sm inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar</button>
          </div>
        </div>
      )}
      {items === null ? <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-navy" /></div>
        : items.length === 0 ? <div className="py-6 text-center text-nardo-400 text-sm">Aún no has enviado comunicados.</div>
        : (
          <div className="space-y-2" data-testid="club-com-list">
            {items.map((a) => (
              <div key={a.id} className="flex items-start gap-3 border border-nardo-100 rounded-lg p-3" data-testid={`club-com-${a.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-navy">{a.title}</div>
                  {a.body && <div className="text-sm text-nardo-600 mt-0.5 whitespace-pre-wrap">{a.body}</div>}
                  {a.file_url && <a href={`${API}${a.file_url}`} target="_blank" rel="noreferrer" className="text-[11px] text-navy underline mt-1 inline-flex items-center gap-1"><Paperclip className="w-3 h-3" /> {a.file_name || "Adjunto"}</a>}
                  <div className="text-[11px] text-nardo-400 mt-1">{a.created_by_role === "docente" ? "Tú" : a.created_by_name} · {(a.created_at || "").slice(0, 10)}</div>
                  <details className="mt-1.5" data-testid={`club-com-seen-${a.id}`}>
                    <summary className="text-[11px] font-semibold text-emerald-700 cursor-pointer select-none">
                      <CheckCheck className="inline w-3.5 h-3.5 -mt-0.5" /> Visto por {a.seen_count || 0} de {club.students ?? "—"} familias
                    </summary>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(a.seen_by || []).length === 0 ? <span className="text-[11px] text-nardo-400">Nadie lo ha abierto todavía.</span>
                        : a.seen_by.map((s, i) => <span key={i} className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">{s.name} · {s.at ? new Date(s.at).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</span>)}
                    </div>
                  </details>
                  {(a.not_seen || []).length > 0 && (
                    <details className="mt-1" data-testid={`club-com-notseen-${a.id}`}>
                      <summary className="text-[11px] font-semibold text-red-600 cursor-pointer select-none">Falta por ver: {a.not_seen.length} familias</summary>
                      <div className="mt-1 space-y-1">
                        {a.not_seen.map((s, i) => {
                          const d = (s.phone || "").replace(/\D/g, ""); const ph = d.length === 10 ? `52${d}` : d;
                          const txt = encodeURIComponent(`Hola, le recordamos revisar el aviso "${a.title}" del club ${a.club_name || ""} en ZAVIA (Portal de Padres).`);
                          return (
                            <div key={i} className="flex items-center justify-between gap-2 text-[11px] bg-red-50 border border-red-100 rounded-lg px-2 py-1" data-testid={`club-notseen-${a.id}-${i}`}>
                              <span className="text-red-700 truncate">{s.name}</span>
                              {ph ? <a href={`https://wa.me/${ph}?text=${txt}`} target="_blank" rel="noreferrer" className="shrink-0 inline-flex items-center gap-1 text-emerald-700 font-semibold hover:underline"><MessageCircle className="w-3 h-3" /> Recordar</a>
                                : <span className="shrink-0 text-nardo-400">Sin teléfono</span>}
                            </div>
                          );
                        })}
                      </div>
                    </details>
                  )}
                </div>
                {a.created_by_role === "docente" && <button onClick={() => del(a)} data-testid={`club-com-del-${a.id}`} className="text-red-500 hover:bg-red-50 p-1.5 rounded-lg shrink-0"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function DocenteClubs({ profile }) {
  const [clubs, setClubs] = useState(null);
  const [active, setActive] = useState(null);
  const [date, setDate] = useState(today());
  const [students, setStudents] = useState([]);
  const [records, setRecords] = useState({});
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dlRange, setDlRange] = useState("day");
  const [exporting, setExporting] = useState(null);
  const [imgData, setImgData] = useState(null);
  const reportRef = useRef(null);
  useEffect(() => { api.get("/docente/clubs").then(({ data }) => setClubs(data.clubs || [])).catch(() => setClubs([])); }, []);
  const loadAtt = (club, d) => api.get(`/docente/clubs/${club.id}/attendance`, { params: { date: d } })
    .then(({ data }) => { setStudents(data.students || []); setRecords(data.records || {}); setSavedAt(data.saved_at); })
    .catch(() => { setStudents([]); setRecords({}); });
  useEffect(() => { if (active) loadAtt(active, date); }, [active, date]); // eslint-disable-line
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.post(`/docente/clubs/${active.id}/attendance`, { date, records }); setSavedAt(data.saved_at); toast.success("Asistencia guardada"); }
    catch { toast.error("No se pudo guardar"); } finally { setSaving(false); }
  };
  const downloadClubPdf = (rng) => downloadPdf(`/docente/clubs/${active.id}/attendance/pdf`, { params: { range: rng, date }, filename: `club_${active.name}_${rng}_${date}.pdf` });
  const downloadClubImage = async (rng) => {
    setExporting(rng);
    try {
      const { data } = await api.get(`/docente/clubs/${active.id}/attendance/summary`, { params: { range: rng, date } });
      setImgData(data);
      await new Promise((r) => setTimeout(r, 200));
      const png = await toPng(reportRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#ffffff" });
      const a = document.createElement("a"); a.href = png; a.download = `club_${active.name}_${rng}_${date}.png`; a.click();
      toast.success("Imagen descargada");
    } catch { toast.error("No se pudo generar la imagen"); } finally { setExporting(null); }
  };
  const logoSrc = profile?.logo_url ? `${API}${profile.logo_url}` : null;
  const stLabel = { presente: "Presente", retardo: "Retardo", justificado: "Justificado", ausente: "Ausente" };
  if (clubs === null) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-navy" /></div>;

  if (active) {
    return (
      <div data-testid="docente-club-attendance">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => setActive(null)} className="text-nardo-500 hover:text-navy"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex items-center gap-2"><Award className="w-5 h-5 text-navy-light" /><h2 className="font-display font-bold text-lg text-navy">{active.name}</h2></div>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="club-date" className="ml-auto border border-nardo-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-navy" />
        </div>
        {students.length === 0 ? <div className="py-12 text-center text-nardo-500">Este club no tiene alumnos asignados. Pídele a Dirección que agregue alumnos.</div> : (
          <>
            <div className="space-y-2">
              {students.map((s) => (
                <div key={s.id} className="flex items-center gap-2 bg-white rounded-xl border border-nardo-200 p-3" data-testid={`club-student-${s.id}`}>
                  <div className="flex-1 min-w-0"><div className="font-semibold text-navy text-sm truncate">{s.full_name}</div><div className="text-[11px] text-nardo-400">{groupLabel(s.group)}</div></div>
                  <div className="flex gap-1">
                    {CLUB_STATUS.map((st) => (
                      <button key={st.id} onClick={() => setRecords((r) => ({ ...r, [s.id]: st.id }))} data-testid={`club-mark-${s.id}-${st.id}`}
                        className={`text-[11px] px-2.5 py-1.5 rounded-lg font-semibold transition-colors ${records[s.id] === st.id ? st.cls : "bg-nardo-100 text-nardo-500 hover:bg-nardo-200"}`}>{st.label}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3 mt-4">
              <button onClick={save} disabled={saving} data-testid="club-save" className="bg-navy hover:bg-navy-light text-white px-5 py-2.5 rounded-lg text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar asistencia</button>
              {savedAt && <span className="text-[11px] text-nardo-400">Último guardado: {new Date(savedAt).toLocaleString("es-MX")}</span>}
            </div>

            <div className="mt-5 bg-white rounded-2xl border border-nardo-200 shadow-sm p-4" data-testid="club-downloads">
              <div className="text-[10px] uppercase tracking-[0.2em] text-navy-light mb-2 flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> Descargar expediente de asistencia</div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex rounded-lg bg-nardo-100 p-1">
                  {CLUB_RANGES.map((r) => (
                    <button key={r.id} onClick={() => setDlRange(r.id)} data-testid={`club-range-${r.id}`} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${dlRange === r.id ? "bg-navy text-white" : "text-nardo-600"}`}>{r.label}</button>
                  ))}
                </div>
                <div className="flex gap-2 ml-auto">
                  <button onClick={() => downloadClubPdf(dlRange)} data-testid="club-dl-pdf" className="text-xs inline-flex items-center gap-1.5 border border-navy text-navy hover:bg-navy hover:text-white px-3 py-2 rounded-lg font-semibold transition-colors"><FileText className="w-4 h-4" /> PDF</button>
                  <button onClick={() => downloadClubImage(dlRange)} disabled={!!exporting} data-testid="club-dl-image" className="text-xs inline-flex items-center gap-1.5 border border-navy text-navy hover:bg-navy hover:text-white px-3 py-2 rounded-lg font-semibold disabled:opacity-50 transition-colors">{exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} Imagen</button>
                </div>
              </div>
              <p className="text-[11px] text-nardo-400 mt-2">Descarga por día, semana, quincena o mes en PDF o imagen. Todo queda guardado como expediente del club.</p>
            </div>
          </>
        )}

        <ClubComunicadosDocente club={active} />


        {/* Nodo imprimible oculto para exportar a imagen */}
        {imgData && (
          <div style={{ position: "fixed", left: -99999, top: 0 }} aria-hidden>
            <div ref={reportRef} style={{ width: 760, background: "#ffffff", padding: 32, fontFamily: "Manrope, Arial, sans-serif", color: "#112240" }} data-testid="club-report-node">
              <div style={{ display: "flex", alignItems: "center", gap: 14, borderBottom: "3px solid #112240", paddingBottom: 14, marginBottom: 16 }}>
                {logoSrc
                  ? <img src={logoSrc} alt="" crossOrigin="anonymous" style={{ height: 54, width: "auto", objectFit: "contain" }} />
                  : <img src="/zavia-icon.png" alt="ZAVIA" style={{ width: 46, height: 46, objectFit: "contain" }} />}
                <div>
                  <div style={{ fontSize: 12, letterSpacing: 3, textTransform: "uppercase", color: "#1e3a8a" }}>ZAVIA</div>
                  <div style={{ fontSize: 22, fontWeight: 800 }}>{imgData.school_name || profile?.school_name || "Colegio"}</div>
                </div>
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Asistencia de Club · {imgData.club?.name}</div>
              <div style={{ fontSize: 13, color: "#334155", marginBottom: 4 }}>Docente: <b>{imgData.docente_name || "—"}</b></div>
              <div style={{ fontSize: 13, color: "#334155", marginBottom: 14 }}>Periodo: <b>{imgData.label}</b> &nbsp;·&nbsp; Sesiones: <b>{imgData.dates?.length || 0}</b></div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#112240", color: "#fff" }}>
                    <th style={{ textAlign: "center", padding: "8px 6px", width: 36 }}>#</th>
                    <th style={{ textAlign: "left", padding: "8px 10px" }}>Alumno</th>
                    <th style={{ textAlign: "left", padding: "8px 10px", width: 120 }}>Grupo</th>
                    {imgData.rng === "day"
                      ? <th style={{ textAlign: "left", padding: "8px 10px", width: 120 }}>Estado</th>
                      : <>
                        <th style={{ textAlign: "center", padding: "8px 6px" }}>Pres.</th>
                        <th style={{ textAlign: "center", padding: "8px 6px" }}>Ret.</th>
                        <th style={{ textAlign: "center", padding: "8px 6px" }}>Just.</th>
                        <th style={{ textAlign: "center", padding: "8px 6px" }}>Aus.</th>
                        <th style={{ textAlign: "center", padding: "8px 6px" }}>Ses.</th>
                      </>}
                  </tr>
                </thead>
                <tbody>
                  {(imgData.rows || []).map((r, i) => (
                    <tr key={r.id} style={{ background: i % 2 ? "#f6f7f8" : "#fff" }}>
                      <td style={{ textAlign: "center", padding: "7px 6px", color: "#94a3b8" }}>{i + 1}</td>
                      <td style={{ padding: "7px 10px", fontWeight: 600 }}>{r.full_name}</td>
                      <td style={{ padding: "7px 10px", color: "#475569" }}>{r.group_label}</td>
                      {imgData.rng === "day"
                        ? <td style={{ padding: "7px 10px", fontWeight: 700, color: { presente: "#059669", ausente: "#dc2626", retardo: "#d97706", justificado: "#0284c7" }[r.day_status] || "#94a3b8" }}>{stLabel[r.day_status] || "Sin registro"}</td>
                        : <>
                          <td style={{ textAlign: "center", padding: "7px 6px", color: "#059669", fontWeight: 700 }}>{r.counts.presente}</td>
                          <td style={{ textAlign: "center", padding: "7px 6px", color: "#d97706", fontWeight: 700 }}>{r.counts.retardo}</td>
                          <td style={{ textAlign: "center", padding: "7px 6px", color: "#0284c7", fontWeight: 700 }}>{r.counts.justificado}</td>
                          <td style={{ textAlign: "center", padding: "7px 6px", color: "#dc2626", fontWeight: 700 }}>{r.counts.ausente}</td>
                          <td style={{ textAlign: "center", padding: "7px 6px", fontWeight: 700 }}>{r.sessions}</td>
                        </>}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 10, color: "#64748b", marginTop: 14 }}>Asistencia de club capturada por el docente y guardada como expediente del colegio.</div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="docente-clubs">
      <h2 className="font-display font-bold text-lg text-navy flex items-center gap-2 mb-1"><Award className="w-5 h-5 text-navy-light" /> Clubs y talleres</h2>
      <p className="text-xs text-nardo-500 mb-4">Registra la asistencia de los clubs que Dirección te asignó.</p>
      {clubs.length === 0 ? <div className="py-16 text-center text-nardo-500" data-testid="docente-clubs-empty">Aún no tienes clubs asignados.</div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {clubs.map((c) => (
            <button key={c.id} onClick={() => setActive(c)} data-testid={`docente-club-${c.id}`} className="bg-white rounded-2xl border border-nardo-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all p-4 text-left">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-navy-light to-navy text-white flex items-center justify-center mb-2"><Award className="w-5 h-5" /></div>
              <div className="font-bold text-navy">{c.name}</div>
              <div className="text-[11px] text-nardo-500">{c.students} alumno(s)</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AulaVirtual({ profile }) {
  const grades = useMemo(() => {
    const assigned = profile?.groups || [];
    if (assigned.length) return assigned;
    return (profile?.all_grades || []).map((g) => g.grade);
  }, [profile]);
  const [grade, setGrade] = useState("");
  const [filter, setFilter] = useState("");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [grading, setGrading] = useState(null);
  const [viewing, setViewing] = useState(null);

  useEffect(() => { if (!grade && grades.length) setGrade(grades[0]); }, [grades]); // eslint-disable-line

  const load = async () => {
    if (!grade) { setPosts([]); return; }
    setLoading(true);
    try {
      const params = { group: grade };
      if (filter) params.type = filter;
      const { data } = await api.get("/aula/posts", { params });
      setPosts(data.posts || []);
    } catch { toast.error("No se pudieron cargar las publicaciones"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [grade, filter]); // eslint-disable-line

  const doDelete = async () => {
    if (!confirmDel) return;
    try { await api.delete(`/aula/posts/${confirmDel.id}`); toast.success("Publicación eliminada"); setConfirmDel(null); load(); }
    catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <div className="space-y-5" data-testid="docente-aula">
      <div className="doc-glass rounded-2xl p-5 border border-white/50 shadow-lg">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Grupo</label>
            <select data-testid="aula-grade" value={grade} onChange={(e) => setGrade(e.target.value)} className="doc-input min-w-[9rem]">
              {grades.length === 0 && <option value="">Sin grupos</option>}
              {grades.map((g) => <option key={g} value={g}>{groupLabel(g)}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[10rem] flex justify-end">
            <button onClick={() => { setEditing(null); setShowForm(true); }} disabled={!grade} data-testid="aula-new" className="text-xs inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2.5 rounded-lg font-semibold disabled:opacity-50">
              <Plus className="w-4 h-4" /> Nueva publicación
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <button onClick={() => setFilter("")} data-testid="aula-filter-all" className={`text-[11px] px-3 py-1.5 rounded-full transition-colors ${!filter ? "bg-navy text-white" : "bg-white/70 text-nardo-600 border border-nardo-200 hover:border-navy"}`}>Todo</button>
          {AULA_TYPES.map((t) => (
            <button key={t.id} onClick={() => setFilter(t.id)} data-testid={`aula-filter-${t.id}`} className={`text-[11px] inline-flex items-center gap-1 px-3 py-1.5 rounded-full transition-colors ${filter === t.id ? t.badge : "bg-white/70 text-nardo-600 border border-nardo-200 hover:border-navy"}`}>
              <t.icon className="w-3 h-3" /> {t.label}s
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="doc-glass rounded-2xl border border-white/50 shadow-lg py-16 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…</div>
      ) : posts.length === 0 ? (
        <div className="doc-glass rounded-2xl border border-white/50 shadow-lg py-16 text-center" data-testid="aula-empty">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-navy-light to-navy text-white flex items-center justify-center mx-auto mb-3"><Presentation className="w-6 h-6" /></div>
          <p className="text-sm text-nardo-500">Aún no hay publicaciones en este grupo.<br />Crea la primera tarea, aviso o material.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((p) => <AulaPostCard key={p.id} post={p} onEdit={(x) => { setEditing(x); setShowForm(true); }} onDelete={setConfirmDel} onGrade={setGrading} onViews={setViewing} />)}
        </div>
      )}

      {showForm && <AulaForm group={grade} initial={editing} onSaved={() => { setShowForm(false); setEditing(null); load(); }} onCancel={() => { setShowForm(false); setEditing(null); }} />}

      {grading && <AulaSubmissionsModal post={grading} onClose={() => setGrading(null)} onGraded={load} />}
      {viewing && <AulaViewsModal post={viewing} onClose={() => setViewing(null)} />}

      {confirmDel && (
        <div className="fixed inset-0 z-[85] bg-navy-ink/70 flex items-center justify-center p-4" onClick={() => setConfirmDel(null)} data-testid="aula-confirm-del">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="font-display font-bold text-navy mb-2">Eliminar publicación</div>
            <p className="text-sm text-nardo-600 mb-4">¿Seguro que deseas eliminar “{confirmDel.title}”? Esta acción no se puede deshacer.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="text-xs text-nardo-600 hover:text-navy px-3 py-2">Cancelar</button>
              <button onClick={doDelete} data-testid="aula-confirm-del-btn" className="text-xs inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold"><Trash2 className="w-4 h-4" /> Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Placeholder próxima fase ---------------- */
function ComingSoon({ icon: Icon, title, desc }) {
  return (
    <div className="doc-glass rounded-2xl border border-white/50 shadow-lg p-10 text-center" data-testid="docente-coming-soon">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-navy-light to-navy text-white flex items-center justify-center mx-auto mb-4"><Icon className="w-7 h-7" /></div>
      <h3 className="font-display font-bold text-xl text-navy mb-2">{title}</h3>
      <p className="text-sm text-nardo-500 max-w-md mx-auto">{desc}</p>
      <span className="inline-block mt-4 text-[11px] uppercase tracking-[0.2em] text-navy-light bg-navy/10 rounded-full px-3 py-1">Próxima fase</span>
    </div>
  );
}

/* ---------------- Inicio (perfil) ---------------- */
const PICKUP_MODE_LABEL = { auto: "En auto", pie: "A pie", moto: "En moto" };

function SalidasGrupo() {
  const [pickups, setPickups] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const timer = useRef(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/docente/pickups"); setPickups(data.pickups || []); }
    catch { /* noop */ } finally { setLoaded(true); }
  }, []);
  useEffect(() => {
    load();
    timer.current = setInterval(load, 15000);
    return () => clearInterval(timer.current);
  }, [load]);
  if (!loaded) return null;
  return (
    <div className="doc-glass rounded-2xl border border-white/50 shadow-lg p-5" data-testid="docente-salidas-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold text-navy flex items-center gap-2"><LogOut className="w-5 h-5 text-navy-light" /> Salidas de mi grupo · hoy</h3>
        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-navy/10 text-navy" data-testid="docente-salidas-count">{pickups.length}</span>
      </div>
      {pickups.length === 0 ? (
        <div className="text-sm text-nardo-500 py-3">Sin salidas anticipadas por ahora. Aquí aparecerán en vivo cuando un tutor las programe.</div>
      ) : (
        <div className="space-y-2">
          {pickups.map((p) => (
            <div key={p.id} className="flex items-start gap-3 bg-white/70 border border-white/60 rounded-xl p-3" data-testid={`docente-salida-${p.id}`}>
              <div className="w-10 h-10 rounded-xl shrink-0 bg-navy/10 text-navy flex items-center justify-center font-display font-bold">{p.student_name?.[0] || "A"}</div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-navy text-sm truncate">{p.student_name}</div>
                <div className="text-xs text-nardo-500">{[p.grade && p.grade !== "—" ? p.grade : null, p.level].filter(Boolean).join(" · ")}</div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-nardo-600 mt-1">
                  <span className="inline-flex items-center gap-1"><Car className="w-3.5 h-3.5 text-navy" /> {PICKUP_MODE_LABEL[p.mode] || p.mode}</span>
                  {p.eta && <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-navy" /> {p.eta}</span>}
                  {p.status === "preparando" && <span className="text-orange-600 font-medium">Preparando salida…</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Inicio({ profile, onGo, reload }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const photoSrc = profile?.photo_url ? `${API}${profile.photo_url}` : null;
  const onPickPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error("La imagen no debe superar 6 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post("/docente/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto de perfil actualizada");
      reload && reload();
    } catch { toast.error("No se pudo subir la foto"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const info = [
    { k: "Correo institucional", v: profile?.email },
    { k: "Materia / cargo", v: profile?.subject || "—" },
    { k: "Grupos asignados", v: (profile?.groups || []).length ? profile.groups.map(groupLabel).join(" · ") : "Todos" },
    { k: "Alumnos a tu cargo", v: profile?.students_count ?? "—" },
    { k: "Colegio", v: profile?.school_name },
    { k: "Alta", v: profile?.created_at ? new Date(profile.created_at).toLocaleDateString("es-MX") : "—" },
  ];
  return (
    <div className="space-y-5" data-testid="docente-inicio">
      <div className="doc-glass rounded-2xl border border-white/50 shadow-lg p-6">
        <div className="flex items-center gap-4">
          <div className="relative shrink-0">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickPhoto} data-testid="docente-photo-input" />
            {photoSrc
              ? <img src={photoSrc} alt={profile?.name} className="w-16 h-16 rounded-2xl object-cover shadow-lg border border-white/60" data-testid="docente-avatar-img" />
              : <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-navy-light to-navy text-white flex items-center justify-center text-2xl font-display font-bold shadow-lg">{(profile?.name || "D")[0]}</div>}
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="docente-photo-edit" title="Cambiar foto de perfil"
              className="absolute -bottom-1.5 -right-1.5 w-7 h-7 rounded-full bg-navy hover:bg-navy-light text-white flex items-center justify-center shadow-md border-2 border-white disabled:opacity-60">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
            </button>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-navy-light flex items-center gap-1"><Sparkles className="w-3 h-3" /> Docente</div>
            <h2 className="font-display font-extrabold text-2xl text-navy leading-tight">{profile?.name || "—"}</h2>
            <div className="text-sm text-nardo-500">{profile?.subject || "Docente"} · {profile?.school_name}</div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
          {info.map((x) => (
            <div key={x.k} className="bg-white/60 border border-white/50 rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-[0.15em] text-nardo-400">{x.k}</div>
              <div className="text-sm text-navy font-medium mt-0.5 break-words">{x.v}</div>
            </div>
          ))}
        </div>
      </div>
      {(profile?.titular_groups || []).length > 0 && <SalidasGrupo />}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {NAV.filter((n) => n.id !== "inicio").map((n) => (
          <button key={n.id} onClick={() => onGo(n.id)} data-testid={`inicio-go-${n.id}`} className="doc-glass border border-white/50 rounded-xl p-4 text-left hover:shadow-lg hover:-translate-y-0.5 transition-all">
            <n.icon className="w-6 h-6 text-navy-light mb-2" />
            <div className="text-sm font-semibold text-navy">{n.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Docente() {
  const { user, logout } = useAuth();
  const [tab, setTabRaw] = useState(() => sessionStorage.getItem("zv_tab_docente") || "inicio");
  const setTab = (t) => { try { sessionStorage.setItem("zv_tab_docente", t); } catch { /* noop */ } setTabRaw(t); };
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    applyTheme(user?.theme_palette, user?.theme_btn, {
      title: user?.theme_title_color, body: user?.theme_text_color, btn: user?.theme_btn_text_color,
    }, user?.theme_logo_colors, user?.theme_panel_color, user?.theme_custom_color);
  }, [user]);

  const loadProfile = async () => { try { const { data } = await api.get("/docente/profile"); setProfile(data); } catch { /* noop */ } };
  useEffect(() => { loadProfile(); }, []); // eslint-disable-line

  if (user && user.role !== "docente") return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-nardo-50 flex relative overflow-x-clip">
      <LogoSplash sessionKey="docente" />
      <style>{`
        .doc-glass{ background: rgba(255,255,255,.82); backdrop-filter: blur(18px) saturate(140%); -webkit-backdrop-filter: blur(18px) saturate(140%);}
        @supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))){ .doc-glass{ background: rgba(255,255,255,.95);} }
        .doc-input{ border:1px solid rgba(255,255,255,.6); background: rgba(255,255,255,.55); border-radius:.6rem; padding:.5rem .75rem; font-size:.85rem; color:rgb(var(--brand-rgb,17 34 64)); outline:none;}
        .doc-input:focus{ box-shadow:0 0 0 2px rgba(var(--brand-light-rgb,30 58 138)/.25);}
      `}</style>
      <ThemedBackground bgId={user?.theme_bg} speed={user?.theme_bg_speed} watermarkUrl={user?.watermark_url ? `${API}${user.watermark_url}` : (user?.logo_url ? `${API}${user.logo_url}` : null)} />
      <ChatWidget />
      <PushToggle floating />
      <NotificationCenter floating />
      <FabDock />

      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-white/40 doc-glass relative z-10 md:sticky md:top-0 md:h-screen md:overflow-y-auto">
        <div className="px-6 py-6 border-b border-white/40 flex items-center gap-3">
          {profile?.logo_url ? (
            <>
              <img src={`${API}${profile.logo_url}`} alt="" className="h-9 w-auto max-w-[120px] object-contain shrink-0" />
              {profile?.logo_show_name && profile?.school_name && (
                <span className="font-display font-bold text-navy text-base truncate">{profile.school_name}</span>
              )}
            </>
          ) : (
            <div className="font-display font-extrabold text-navy text-lg truncate">{profile?.school_name || "Colegio"}</div>
          )}
        </div>
        <nav className="flex-1 px-3 py-6 flex flex-col gap-1" data-testid="docente-nav">
          {NAV.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`docente-nav-${id}`}
              className={`group flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${tab === id ? "bg-navy text-white shadow" : "text-nardo-600 hover:text-navy hover:bg-white/60"}`}>
              <Icon className="w-4 h-4" strokeWidth={1.75} /> <span>{label}</span>
            </button>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-white/40">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-navy-light to-navy text-white flex items-center justify-center font-display font-bold">{(user?.name || "D")[0]}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate text-navy">{user?.name}</div>
              <div className="text-xs text-nardo-500 truncate">Docente</div>
            </div>
          </div>
          <button data-testid="docente-logout" onClick={logout} className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.2em] text-nardo-600 hover:text-navy border border-nardo-200 hover:border-navy transition-colors rounded-lg">
            <LogOut className="w-3.5 h-3.5" strokeWidth={2} /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 relative z-10">
        {/* Mobile header */}
        <header className="md:hidden sticky top-0 z-40 doc-glass border-b border-white/40 px-4 py-3 flex items-center justify-between gap-3">
          <div className="font-display font-bold text-navy truncate flex items-center gap-2"><GraduationCap className="w-5 h-5 text-navy-light" /> {user?.name}</div>
          <button onClick={logout} data-testid="docente-logout-mobile" className="text-xs uppercase tracking-widest text-nardo-600">Salir</button>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-6 sm:py-8 pb-36 md:pb-8 relative bg-nardo-50/85 md:bg-transparent">
          <div className="max-w-5xl mx-auto w-full">
            <PushPrompt />
            <div className="mb-6">
              <div className="text-[11px] uppercase tracking-[0.3em] text-navy-light mb-2 flex items-center gap-2 flex-wrap"><span className="w-8 h-px bg-navy" /> Portal del docente{(profile?.portal_subtitle || user?.portal_subtitle) ? ` · ${profile?.portal_subtitle || user?.portal_subtitle}` : ""}</div>
              <h1 className="font-display font-extrabold text-2xl sm:text-4xl tracking-tight text-navy flex items-center gap-3">
                {React.createElement(NAV.find((n) => n.id === tab)?.icon || Home, { className: "w-7 h-7 sm:w-8 sm:h-8 text-navy-light shrink-0", strokeWidth: 1.5 })}
                <span className="min-w-0">{NAV.find((n) => n.id === tab)?.label}</span>
              </h1>
            </div>

            {tab === "inicio" && <Inicio profile={profile} onGo={setTab} reload={loadProfile} />}
            {tab === "asistencia" && <Asistencia profile={profile} />}
            {tab === "aula" && <AulaVirtual profile={profile} />}
            {tab === "recursos" && <BancoRecursos profile={profile} />}
            {tab === "planeaciones" && <Planeaciones profile={profile} />}
            {tab === "dosificacion" && <Dosificacion profile={profile} />}
            {tab === "clubs" && <DocenteClubs profile={profile} />}
            {tab === "calificaciones" && <LibroCalificaciones profile={profile} />}
            {tab === "boletas" && <Boletas profile={profile} />}
            {tab === "incidencias" && <Incidencias profile={profile} />}
            {tab === "mensajes" && <DocenteMessages />}
            {tab === "clases" && <ComingSoon icon={MonitorPlay} title="Clases en línea" desc="Comparte tu enlace de Zoom o Google Meet, o crea una sala del sistema, con un panel moderno y fondo animado mientras tus alumnos se conectan. Disponible en la próxima fase." />}
            {tab === "reunion" && <Comunicacion embedded />}
            {tab === "musica" && <MusicModule />}
            <SiteFooter />
          </div>
        </main>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl doc-glass border-t border-white/40 overflow-x-auto no-scrollbar" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 18px)" }} data-testid="docente-mobile-nav">
          <div className="flex w-max">
            {NAV.map(({ id, label, icon: Icon }) => (
              <button key={id} onClick={() => setTab(id)} data-testid={`docente-nav-${id}-mobile`} className={`flex flex-col items-center justify-center gap-1 py-2.5 px-1 shrink-0 w-[5rem] text-center text-[10px] font-medium uppercase tracking-wide leading-tight ${tab === id ? "text-navy" : "text-nardo-500"}`}>
                <span className={`flex items-center justify-center w-9 h-9 rounded-full ${tab === id ? "bg-navy text-white" : ""}`}><Icon className="w-[18px] h-[18px]" strokeWidth={1.75} /></span>
                <span className="leading-tight">{label}</span>
              </button>
            ))}
          </div>
        </nav>
      </div>
    </div>
  );
}
